import { useState, useMemo, useEffect, useRef } from "react";
import { formatMuteDate, muteReasonLabel } from "@components/chat/useClientMute";
import "./booking.css";
import TextField from "@components/ui/textfields/TextField";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import { bookingsApi } from "@services/api/bookings";
import { ordresApi } from "@services/api/ordres";
import { recallCampaignsApi } from "@services/api/recallCampaigns";
import { interventionsApi, type Intervention } from "@services/api/interventions";
import { searchDossiersFull, searchPlatesFull, type ProjectHit } from "@services/algolia";
import { dossiersApi } from "@services/api/dossiers";
import { vehiclesApi } from "@services/api/vehicles";
import { clientVehiclesApi } from "@services/api/clientVehicles";
import type { ClientVehicle, VehiclePartyBlock } from "types/client_vehicle";
import type { BookingPromotion } from "types/org";
import { useDataStore } from "@store/user_org";
import { Switch } from "@components/ui/selection/Switch";
import { showNotification } from "context/notification.service";
import VehicleImage from "@components/ui/VehicleImage";
import CarteGriseUploadOverlay from "@components/CarteGriseUploadOverlay";
import type { CarteGriseValidationResult } from "@services/api/tracking";

/** Longueur minimale d'une précision « obligatoire » (diagnostic…) : en dessous,
 *  « bruit » ou « voyant » ne dit pas à l'atelier quoi chercher (demande garage 2026-10-01). */
// Prise de RDV interne : la précision reste obligatoire mais sans longueur minimum —
// les 50 caractères imposés gênaient l'accueil (Steeven, 07/10/2026).
const MIN_DETAILS_CHARS = 1;


/** Minimal org-config shape this page needs (built from orgData below). */
interface OrgConfig {
  name: string;
  address?: string;
  postal?: string;
  city?: string;
  booking_min_days_ahead?: number;
  booking_max_days_ahead?: number;
  booking_sr_min_hours_ahead?: number;
  booking_safety_margin_hours?: number;
  booking_mobility_options?: MobilityOption[];
}

type MobilityOption = { key: string; label: string; sub: string };

/** Fixed keys (velo|pret|location) drive the fleet-usage/availability logic and must
 *  never change; only label + sub are garage-editable (Réglages → Prise de RDV). */
function resolveMobilityOptions(cfg?: MobilityOption[]): MobilityOption[] {
  if (!cfg?.length) return DEFAULT_MOBILITY_OPTIONS;
  return DEFAULT_MOBILITY_OPTIONS.map((d) => {
    const o = cfg.find((c) => c.key === d.key);
    return o ? { key: d.key, label: o.label || d.label, sub: o.sub ?? d.sub } : d;
  });
}

// ─── Types ──────────────────────────────────────────────────

export interface BookingData {
  immatriculation: string;
  km: string;
  service: string;
  intervention_type: string;
  intervention: string;
  /** Full list of selected intervention names (multi-select across categories). */
  interventions: string[];
  custom_intervention: string;
  /** Interne : travaux de l'atelier sur un véhicule maison. Aucune charge n'est posée
   *  au planning atelier — voir booking_controller.auto_place_booking_charge. */
  internal_work: boolean;
  arrived_by_assistance: boolean;
  /** Free-text precision per intervention name (interventions flagged requires_details). */
  intervention_details: Record<string, string>;
  dont_know_service: boolean;
  deposit_date: string | null;
  deposit_time: string;
  restitution_date: string | null;
  restitution_time: string;
  wait_on_site: boolean | null;
  mobility_option: string;
  phone: string;
  email: string;
  address: string;
  nom: string;
  prenom: string;
  privacy_accepted: boolean;
  /** Internal: how to notify the client — "whatsapp" | "email". */
  communication_channel: string;
  /** Internal: met le client en sourdine COMPLÈTE (aucun email / WhatsApp / SMS,
   *  automatique comme manuel) jusqu'à réactivation manuelle depuis le chat. */
  mute_client: boolean;
  /** Internal: staff-chosen loaner vehicle id ("" = auto-assign). */
  rental_vehicle_id: string;
  /** Internal: a known client was picked at the vehicle step — skip the
   *  phone re-lookup on the final contact step. */
  client_selected: boolean;
  /** Internal: the address was prefilled from a known client's saved contact, so
   *  the address field is hidden. Tracked separately from `address` so that
   *  typing a fresh address does NOT make the field vanish mid-input. */
  address_prefilled: boolean;
  /** Internal: « Changement de propriétaire » on a chosen plate — picking the new
   *  owner keeps that plate, and the car is re-attached to them on booking. */
  owner_change: boolean;
  /** Internal: the known client (dossier) the RDV goes to — picked by name/plate or
   *  recognised by phone. Without it the server matched on the dossier's phone only
   *  and a known client without that number got a duplicate dossier. */
  dossier_id: string;
  /** Internal (UI only, never sent): the plate picked belongs to a vehicle linked to
   *  several parties (propriétaire / société / conducteur) and staff has not yet
   *  chosen which one pays — i.e. who is the client of the RDV. */
  payer_pending: boolean;
}

export const INITIAL: BookingData = {
  immatriculation: "",
  km: "",
  service: "",
  intervention_type: "",
  intervention: "",
  interventions: [],
  custom_intervention: "",
  internal_work: false,
  arrived_by_assistance: false,
  intervention_details: {},
  dont_know_service: false,
  deposit_date: null,
  deposit_time: "",
  restitution_date: null,
  restitution_time: "",
  wait_on_site: null,
  mobility_option: "",
  phone: "",
  email: "",
  address: "",
  nom: "",
  prenom: "",
  privacy_accepted: false,
  communication_channel: "whatsapp",
  mute_client: false,
  rental_vehicle_id: "",
  client_selected: false,
  address_prefilled: false,
  owner_change: false,
  dossier_id: "",
  payer_pending: false,
};

const SERVICES = [
  { value: "mecanique", label: "Mécanique" },
  { value: "carrosserie", label: "Carrosserie" },
  { value: "vitrage", label: "Vitrage" },
  { value: "pneumatique", label: "Pneumatique" },
];

interface BookingHours { opening: number; closing: number; lunch_start: number; lunch_end: number }

/**
 * Grille des heures de dépôt proposées, bâtie sur les horaires du garage.
 *
 * Elle était figée à 08:00-11:00 / 14:00-18:00 alors que le back calcule ses créneaux
 * de l'ouverture à la fermeture, pause déjeuner exclue. Chez Héric (8h-18h, pause
 * 12h-13h) les créneaux 11h15-11h45 et 13h-13h45 étaient donc réservables sans jamais
 * être affichés : le jour dont ils étaient les derniers libres restait proposé au
 * calendrier puis annonçait « Aucun créneau disponible ce jour ».
 */
function buildSlotGrid(hours?: BookingHours) {
  const opening = hours?.opening ?? 8;
  const closing = hours?.closing ?? 18;
  const lunchStart = hours?.lunch_start ?? 12;
  const lunchEnd = hours?.lunch_end ?? 13;
  const morning: string[] = [];
  const afternoon: string[] = [];
  // Pas de 15 min, fermeture incluse — mêmes bornes que get_unavailable_slots.
  for (let m = Math.round(opening * 60); m <= Math.round(closing * 60); m += 15) {
    const h = m / 60;
    if (h >= lunchStart && h < lunchEnd) continue;
    const label = `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
    (h < lunchStart ? morning : afternoon).push(label);
  }
  return { morning, afternoon, all: [...morning, ...afternoon] };
}

/** Horaires de réservation du garage, mêmes priorités que _get_org_hours côté back. */
export function useSlotGrid() {
  const orgData = useDataStore((s) => s.orgData);
  return useMemo(() => buildSlotGrid({
    opening: orgData?.config?.booking_opening_hour || orgData?.infos?.work_hours?.start || 8,
    closing: orgData?.config?.booking_closing_hour || orgData?.infos?.work_hours?.end || 18,
    lunch_start: orgData?.config?.booking_lunch_start ?? orgData?.infos?.lunch_break?.start ?? 12,
    lunch_end: orgData?.config?.booking_lunch_end ?? orgData?.infos?.lunch_break?.end ?? 13,
  }), [orgData]);
}

/** Service retenu quand aucun n'est choisi. Le calendrier et la grille horaire doivent
 *  partir du même : sans service, full-days ne barrait un jour que si mécanique ET
 *  carrosserie saturaient, alors que les créneaux étaient calculés sur la mécanique
 *  seule — d'où des jours proposés puis vides. */
const DEFAULT_SERVICE = "mecanique";

// Steps are dynamic: the "restitution" (date/heure de récupération) step only
// exists when the vehicle is left at the garage. When the client waits on site
// the restitution is auto-computed, so that step is skipped entirely.
type StepId = "service" | "vehicle" | "date" | "mobility" | "restitution" | "recap" | "contact" | "cartegrise";
function buildSteps(isShort: boolean, waitOnSite: boolean | null): StepId[] {
  const skipRestitution = isShort && waitOnSite === true;
  return [
    "service", "vehicle", "date", "mobility",
    ...(skipRestitution ? [] : ["restitution" as StepId]),
    "recap", "contact", "cartegrise",
  ];
}

export const NEED_CARDS = [
  { id: "entretien",          iconType: "entretien",          label: "Entretien & révision",  desc: "Vidange, filtres, contrôle général...",         service: "mecanique",   type: "entretien" },
  { id: "reparation",         iconType: "reparation",         label: "Réparation",            desc: "Freins, embrayage, distribution...",            service: "mecanique",   type: "reparation" },
  { id: "diagnostic",         iconType: "diagnostic",         label: "Diagnostic",            desc: "Recherche de panne, voyant...",                 service: "mecanique",   type: "diagnostic" },
  { id: "controle_technique", iconType: "controle_technique", label: "Contrôle technique",    desc: "CT, contre-visite...",                          service: "mecanique",   type: "controle_technique" },
  { id: "pneumatique",        iconType: "pneumatique",        label: "Pneumatiques",          desc: "Changement, équilibrage, réparation...",        service: "pneumatique", type: "entretien" },
  { id: "carrosserie",        iconType: "carrosserie",        label: "Carrosserie",           desc: "Carrosserie, échappement, amortisseurs...",     service: "carrosserie", type: "reparation" },
  { id: "rappel_constructeur", iconType: "rappel_constructeur", label: "Rappel constructeur",  desc: "Campagne de rappel, courrier du constructeur...", service: "mecanique",  type: "reparation" },
];

/** Mobility key -> illustration file (public/illus). */
const MOBILITY_ILLUS: Record<string, string> = { velo: "velo", pret: "pret", location: "location", aucun: "aucun" };

const DEFAULT_MOBILITY_OPTIONS: MobilityOption[] = [
  { key: "velo",     label: "Vélo électrique",       sub: "Gratuit" },
  { key: "pret",     label: "Véhicule de prêt",      sub: "Véhicule 15-20 ans, limité à 100 km" },
  { key: "location", label: "Véhicule de location",  sub: "Véhicule récent, km illimités — 25€/jour" },
  { key: "aucun",    label: "Aucun",                  sub: "" },
];

export const SHORT_THRESHOLD = 150;

/** Interventions of this type are manufacturer recall campaigns ("Rappel constructeur",
 *  a.k.a. Campagne). They are worked in the atelier's own `campagnes` zone. */
export const CAMPAGNE_INTERVENTION_TYPE = "rappel_constructeur";

/** Which atelier zone the selected basket must be checked against.
 *
 *  Campagne ALONE → the campagnes zone drives availability. As soon as other work is
 *  booked with it the campaign is ignored and the other interventions decide: asking
 *  the campagnes zone for a vidange would offer slots no mécanicien can honour. A
 *  campagne-only booking is never "service rapide" either — it has its own zone and
 *  keeps the standard booking lead time. */
export function zoneFlags(selected: Intervention[], customIntervention: string) {
  const isCampagne =
    selected.length > 0 &&
    !customIntervention.trim() &&
    selected.every((i) => i.type === CAMPAGNE_INTERVENTION_TYPE);
  const zoneIntvs = isCampagne ? selected : selected.filter((i) => i.type !== CAMPAGNE_INTERVENTION_TYPE);
  return {
    isCampagne,
    isFast: !isCampagne && zoneIntvs.length > 0 && zoneIntvs.every((i) => i.fast),
    isExpress: zoneIntvs.length > 0 && zoneIntvs.every((i) => i.express_24h),
  };
}

// ─── Utility functions ──────────────────────────────────────

function formatEstimatedDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < SHORT_THRESHOLD) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
  }
  const days = Math.ceil(minutes / 480);
  return `${days} jour${days > 1 ? "s" : ""}`;
}

/** Wait shown on the "j'attends" option. Always hours/minutes, never days:
 *  waiting on site is only offered for short interventions. */
function formatWaitDuration(minutes: number): string {
  if (minutes <= 0) return "";
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h} h ${String(m).padStart(2, "0")}` : `${h} h`;
}

function slotToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

function computeRestitutionTime(depositTime: string, totalMinutes: number): string {
  const [h, m] = depositTime.split(":").map(Number);
  const total = h * 60 + m + totalMinutes;
  const rh = Math.floor(total / 60);
  const rm = total % 60;
  if (rh >= 18) return "18:00";
  const rounded = Math.ceil(rm / 15) * 15;
  const fh = rounded >= 60 ? Math.min(rh + 1, 18) : rh;
  const fm = rounded >= 60 ? 0 : rounded;
  return `${String(fh).padStart(2, "0")}:${String(fm).padStart(2, "0")}`;
}

function addBusinessDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T12:00:00");
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) added++;
  }
  return toLocalDateStr(d);
}

/** Format a Date as YYYY-MM-DD in local timezone (avoids UTC shift from toISOString) */
function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Earliest bookable date — mirrors StepDateTime's minDate so the prefetch warms
 *  the same day the date step will auto-select. */
function computeMinDate(minDaysAhead: number, isFast: boolean, srMinHoursAhead: number): string {
  if (isFast) return toLocalDateStr(new Date(Date.now() + srMinHoursAhead * 60 * 60 * 1000));
  const d = new Date(); d.setDate(d.getDate() + minDaysAhead);
  return toLocalDateStr(d);
}

/** First non-full weekday in [minDate, maxDate] — mirrors StepDateTime's auto-select. */
function firstCandidateDate(fullDays: string[], minDate: string, maxDate: string): string | null {
  const fullSet = new Set(fullDays);
  const d = new Date(minDate + "T12:00:00");
  const max = new Date(maxDate + "T12:00:00");
  for (let i = 0; i < 60; i++) {
    if (d > max) break;
    const ds = toLocalDateStr(d);
    if (d.getDay() !== 0 && d.getDay() !== 6 && !fullSet.has(ds)) return ds;
    d.setDate(d.getDate() + 1);
  }
  return null;
}

function formatSlot(slot: string): string {
  return slot.replace(":", "h");
}

function formatDateFr(d: string | null): string {
  if (!d) return "--";
  const date = new Date(d + "T12:00:00");
  return date.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function formatDateShort(d: string | null): string {
  if (!d) return "--";
  const parts = d.split("-");
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

// ─── Service icons ──────────────────────────────────────────

function ServiceIcon({ type, size = 26, color = "currentColor" }: { type: string; size?: number; color?: string }) {
  const s: React.CSSProperties = { width: size, height: size, flexShrink: 0 };
  const p = { stroke: color, strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, fill: "none" };
  if (type === "entretien") return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg>;
  if (type === "reparation") return <svg style={s} viewBox="0 0 24 24" {...p}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>;
  if (type === "diagnostic") return <svg style={s} viewBox="0 0 24 24" {...p}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>;
  if (type === "controle_technique") return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></svg>;
  if (type === "pneumatique") return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" /><line x1="12" y1="2" x2="12" y2="9" /><line x1="12" y1="15" x2="12" y2="22" /><line x1="2" y1="12" x2="9" y2="12" /><line x1="15" y1="12" x2="22" y2="12" /></svg>;
  if (type === "carrosserie") return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M6 10l2.5-5h7L18 10" /><rect x="2" y="10" width="20" height="7" rx="2" /><circle cx="7" cy="17" r="2" fill={color} stroke="none" /><circle cx="17" cy="17" r="2" fill={color} stroke="none" /><line x1="12" y1="5" x2="12" y2="10" /></svg>;
  return <span style={s} />;
}


// ─── Calendar ───────────────────────────────────────────────

const DAYS_HEADER = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];

function MiniCalendar({
  selectedDate, onSelect, minDate, maxDate, fullDays, onMonthChange, rangeStart,
}: {
  selectedDate: string | null;
  onSelect: (date: string) => void;
  minDate?: string;
  maxDate?: string;
  fullDays?: string[] | null;
  onMonthChange?: (monthStr: string) => void;
  /** Deposit date: shown as the range start, with a band through to selectedDate (immobilization). */
  rangeStart?: string | null;
}) {
  const [viewMonth, setViewMonth] = useState(() => {
    const ref = selectedDate || minDate;
    if (ref) { const d = new Date(ref + "T12:00:00"); return { year: d.getFullYear(), month: d.getMonth() }; }
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  useEffect(() => {
    onMonthChange?.(`${viewMonth.year}-${String(viewMonth.month + 1).padStart(2, "0")}`);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-navigate to the selected date's month
  useEffect(() => {
    if (!selectedDate) return;
    const d = new Date(selectedDate + "T12:00:00");
    const m = { year: d.getFullYear(), month: d.getMonth() };
    if (m.year !== viewMonth.year || m.month !== viewMonth.month) {
      setViewMonth(m);
      onMonthChange?.(`${m.year}-${String(m.month + 1).padStart(2, "0")}`);
    }
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeMonth = (next: { year: number; month: number }) => {
    setViewMonth(next);
    onMonthChange?.(`${next.year}-${String(next.month + 1).padStart(2, "0")}`);
  };

  const monthLabel = useMemo(() => {
    const d = new Date(viewMonth.year, viewMonth.month, 1);
    return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  }, [viewMonth]);

  const weeks = useMemo(() => {
    const first = new Date(viewMonth.year, viewMonth.month, 1);
    const lastDay = new Date(viewMonth.year, viewMonth.month + 1, 0).getDate();
    let startDow = first.getDay();
    startDow = startDow === 0 ? 6 : startDow - 1;
    const cells: { day: number; inMonth: boolean; dateStr: string }[] = [];
    const prevMonthLast = new Date(viewMonth.year, viewMonth.month, 0).getDate();
    for (let i = startDow - 1; i >= 0; i--) {
      const d = prevMonthLast - i;
      const m = viewMonth.month === 0 ? 11 : viewMonth.month - 1;
      const y = viewMonth.month === 0 ? viewMonth.year - 1 : viewMonth.year;
      cells.push({ day: d, inMonth: false, dateStr: `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
    }
    for (let d = 1; d <= lastDay; d++) {
      cells.push({ day: d, inMonth: true, dateStr: `${viewMonth.year}-${String(viewMonth.month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
    }
    const remaining = 7 - (cells.length % 7);
    if (remaining < 7) {
      for (let d = 1; d <= remaining; d++) {
        const m = viewMonth.month === 11 ? 0 : viewMonth.month + 1;
        const y = viewMonth.month === 11 ? viewMonth.year + 1 : viewMonth.year;
        cells.push({ day: d, inMonth: false, dateStr: `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
      }
    }
    const rows: typeof cells[] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [viewMonth]);

  const cutoff = minDate || toLocalDateStr(new Date());
  const loadingDays = fullDays === null || fullDays === undefined;
  const fullSet = useMemo(() => new Set(fullDays || []), [fullDays]);
  const todayStr = toLocalDateStr(new Date());

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "2px 0 6px" }}>
        <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 17, lineHeight: "38px", letterSpacing: "-0.015em", color: "#0A0A0A", textTransform: "capitalize" }}>{monthLabel}</span>
        <span style={{ display: "flex", gap: 6 }}>
          <button className="bkui-iconbtn"
            onClick={() => changeMonth(viewMonth.month === 0 ? { year: viewMonth.year - 1, month: 11 } : { year: viewMonth.year, month: viewMonth.month - 1 })}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
          <button className="bkui-iconbtn"
            onClick={() => changeMonth(viewMonth.month === 11 ? { year: viewMonth.year + 1, month: 0 } : { year: viewMonth.year, month: viewMonth.month + 1 })}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
          </button>
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {DAYS_HEADER.map((d) => (
          <span key={d} style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 32, fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: 12, color: "#9CA3AF" }}>{d}</span>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {weeks.map((week, wi) => (
          <div key={wi} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
            {week.map((cell) => {
              const isSelected = selectedDate === cell.dateStr;
              const isToday = cell.dateStr === todayStr && cell.inMonth;
              const dow = new Date(cell.dateStr + "T12:00:00").getDay();
              const isWeekend = dow === 0 || dow === 6;
              const beyondMax = maxDate ? cell.dateStr > maxDate : false;
              const full = cell.inMonth && cell.dateStr >= cutoff && fullSet.has(cell.dateStr);
              const disabled = !cell.inMonth || cell.dateStr < cutoff || beyondMax || full || loadingDays || isWeekend;
              // Immobilization range: deposit day → selected recovery day
              const isDepositDay = !!rangeStart && cell.dateStr === rangeStart;
              const hasRange = !!(rangeStart && selectedDate && selectedDate > rangeStart);
              const inBand = hasRange && cell.dateStr > rangeStart! && cell.dateStr < selectedDate!;
              const isFilled = isSelected || isDepositDay;
              if (!cell.inMonth) return <div key={cell.dateStr} />;
              return (
                <div key={cell.dateStr} style={{ position: "relative", display: "flex", justifyContent: "center" }}>
                  {hasRange && (inBand || isDepositDay || isSelected) && (
                    <div style={{
                      position: "absolute", top: 0, bottom: 0,
                      left: isDepositDay && !inBand ? "50%" : 0,
                      right: isSelected && !inBand ? "50%" : 0,
                      background: "#F3F3F4",
                    }} />
                  )}
                  <button
                    style={{
                      position: "relative",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      width: 40, height: 40, borderRadius: 12,
                      fontFamily: "Inter, sans-serif", fontWeight: isFilled || isToday ? 600 : 400, fontSize: 14,
                      color: isFilled ? "#fff" : disabled ? "#D4D4D4" : isToday ? "#000" : "#4B5563",
                      background: isFilled ? "#000" : "transparent",
                      cursor: disabled ? "default" : "pointer",
                      border: isToday && !isFilled ? "1.5px solid #D1D5DB" : "1.5px solid transparent",
                      transition: "background 0.15s",
                    }}
                    onClick={() => !disabled && onSelect(cell.dateStr)}
                    disabled={disabled}
                  >{cell.day}</button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Step 0 — Service selection ─────────────────────────────

/** Resolve the category card a given intervention name belongs to (real or a "whole service" pseudo-entry). */
function cardForIntervention(name: string, interventions: Intervention[]): typeof NEED_CARDS[0] | undefined {
  const intv = interventions.find((i) => i.name === name);
  if (intv) return NEED_CARDS.find((c) => c.id === intv.type);
  return NEED_CARDS.find((c) => c.label === name);
}


// ─── V2 design helpers ──────────────────────────────────────

/** Illustration for a category / concept. Files live in public/illus. */
function Illus({ name, size = 92, style }: { name: string; size?: number; style?: React.CSSProperties }) {
  return <img src={`/illus/${name}.webp`} alt="" width={size} height={size} style={{ display: "block", ...style }} />;
}

const V2_TITLE: React.CSSProperties = {
  fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 26, lineHeight: 1.12,
  letterSpacing: "-0.032em", color: "#0A0A0A", margin: 0,
};
const V2_LEDE: React.CSSProperties = {
  fontFamily: "Inter, sans-serif", fontSize: 15, lineHeight: 1.45, color: "#6B6B70", margin: 0,
};
const V2_CHECK = (
  <svg width={12} height={12} viewBox="0 0 12 12" fill="none">
    <path d="M2.5 6l2.5 2.5L9.5 4" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const V2_MARK = (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="11" fill="#0A0A0A" />
    <path d="M7.6 12.3l2.9 2.9L16.4 9.3" stroke="#fff" strokeWidth={2.1} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Split "Vidange + filtre huile à partir de 129€ TTC" into label + price. */
function splitPrice(name: string): { label: string; price: number | null; from: boolean } {
  const m = name.match(/\s*(à partir de\s*)?(\d+)\s*€(\s*TTC)?\s*$/i);
  if (!m) return { label: name.replace(/\s+/g, " ").trim(), price: null, from: false };
  return { label: name.replace(m[0], "").replace(/\s+/g, " ").trim(), price: Number(m[2]), from: !!m[1] };
}

/** SIV (since 2009) is AB-123-CD; pre-2009 FNI plates are 1234 AB 56 with no
 *  dashes, so the mask follows the input instead of being forced on every car. */
function formatPlateV2(raw: string): string {
  const v = raw.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  if (!v) return "";
  if (/^[A-Z]/.test(v)) {
    const p = v.slice(0, 7);
    return [p.slice(0, 2), p.slice(2, 5), p.slice(5, 7)].filter(Boolean).join("-");
  }
  const m = v.slice(0, 9).match(/^(\d{1,4})([A-Z]{0,3})(\d{0,2})$/);
  return m ? [m[1], m[2], m[3]].filter(Boolean).join(" ") : v.slice(0, 9);
}

/** « Offre en cours » on top of a category tile: the first thing the eye meets there. */
const PROMO_TAG: React.CSSProperties = {
  alignSelf: "flex-start", fontSize: 11.5, fontWeight: 700, letterSpacing: "0.02em", color: "#0A0A0A",
  background: "#FFD43B", borderRadius: 999, padding: "3px 9px", marginBottom: 2,
};

/** dd/mm from an ISO date, for « jusqu'au 29/11 ». */
function promoEnd(iso?: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}` : "";
}

/** Offers running today (Paris), as the public config serves them — the internal page
 *  reads the raw org config, so it filters here. Twin of booking_promotions.py. */
export function activePromotions(promos?: BookingPromotion[], now: Date = new Date()): BookingPromotion[] {
  const today = now.toLocaleDateString("sv-SE", { timeZone: "Europe/Paris" });
  return (promos || []).filter((p) =>
    p && p.active !== false && (p.title || "").trim() && (p.category || "").trim()
    && !(p.start_date && today < p.start_date.slice(0, 10))
    && !(p.end_date && today > p.end_date.slice(0, 10)));
}

/** A running offer, on top of its category's prestations (cf. widget client). */
function PromoCard({ promo }: { promo: BookingPromotion }) {
  const end = promoEnd(promo.end_date);
  return (
    <div style={{ background: "#FFF7DB", border: "1px solid #F3D36B", borderRadius: 16, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#8A6100" }}>
        Offre en cours{end ? ` · jusqu'au ${end}` : ""}
      </span>
      <span style={{ fontFamily: "Inter, sans-serif", fontSize: 15.5, fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.3, color: "#0A0A0A" }}>{promo.title}</span>
      {promo.text && (
        <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13.5, lineHeight: 1.45, color: "#3F3F46", whiteSpace: "pre-line" }}>{promo.text}</span>
      )}
      {promo.link_url && (
        <a href={promo.link_url} target="_blank" rel="noopener noreferrer"
          style={{ fontFamily: "Inter, sans-serif", fontSize: 13.5, fontWeight: 600, color: "#0A0A0A", textDecoration: "underline", alignSelf: "flex-start", marginTop: 2 }}>
          {promo.link_label || "Voir les conditions de l'offre"} ↗
        </a>
      )}
    </div>
  );
}

export function StepService({
  data, update, interventions, sub, setSub, internalMode = false, promotions = [],
}: {
  data: BookingData;
  update: (k: keyof BookingData, v: unknown) => void;
  interventions: Intervention[];
  sub?: string | null;
  setSub?: (v: string | null) => void;
  /** La case « travaux interne » n'a de sens qu'au guichet : le client, lui, ne prend
   *  pas rendez-vous pour un véhicule du garage. */
  internalMode?: boolean;
  /** Offers running today; shown on their category, like the client widget. */
  promotions?: BookingPromotion[];
}) {
  const selected = useMemo(() => new Set(data.interventions), [data.interventions]);
  const [autreOpen, setAutreOpen] = useState(() => data.custom_intervention.trim().length > 0);
  // Standalone use (BookingDebugScreen) has no parent to hold the drill-in state.
  const [localSub, setLocalSub] = useState<string | null>(null);
  const openCat = sub !== undefined ? sub : localSub;
  const setOpenCat = setSub || setLocalSub;

  // Keep the primary (first) selection in sync for downstream steps / payload.
  // Does NOT touch the "Autre" free text — both coexist.
  const applySelection = (names: string[]) => {
    const primaryCard = names.length ? cardForIntervention(names[0], interventions) : undefined;
    update("interventions", names);
    update("intervention", names[0] || "");
    update("intervention_type", primaryCard?.id || "");
    update("service", primaryCard?.service || "");
  };

  const toggleIntervention = (name: string) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name); else next.add(name);
    applySelection(Array.from(next));
  };

  const needDetails = useMemo(
    () => interventions.filter((i) => selected.has(i.name) && i.requires_details),
    [interventions, selected],
  );
  const setDetail = (name: string, value: string) => {
    update("intervention_details", { ...data.intervention_details, [name]: value });
  };

  const isAutre = autreOpen;

  // Reveal the free-text field when "Autre" opens: the sticky footer overlays the
  // bottom of the scroll area, so autoFocus alone can leave the textarea behind it.
  // The scroller's bottom padding equals the footer height, hence the offset.
  const autreRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!isAutre) return;
    const t = setTimeout(() => {
      const el = autreRef.current;
      const scroller = el?.closest(".bkui-scroll") as HTMLElement | null;
      if (!el || !scroller) return;
      const pad = parseFloat(getComputedStyle(scroller).paddingBottom) || 0;
      const elBottom = el.getBoundingClientRect().bottom - scroller.getBoundingClientRect().top + scroller.scrollTop;
      const target = Math.min(
        Math.max(elBottom + pad - scroller.clientHeight, 0),
        scroller.scrollHeight - scroller.clientHeight,
      );
      if (target > scroller.scrollTop) scroller.scrollTo({ top: target, behavior: "smooth" });
    }, 180);
    return () => clearTimeout(t);
  }, [isAutre]);

  const details = needDetails.map((intv) => {
    const len = (data.intervention_details[intv.name] || "").trim().length;
    const filled = len >= MIN_DETAILS_CHARS;
    return (
    <div key={`detail-${intv.id}`} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 13, color: "#0A0A0A" }}>
        Précisions pour « {intv.name} »{" "}
        <span style={{ color: "#B91C1C", fontWeight: 500 }}>(obligatoire)</span>
      </label>
      <textarea
        value={data.intervention_details[intv.name] || ""}
        onChange={(e) => setDetail(intv.name, e.target.value)}
        placeholder="Décrivez le problème / apportez des précisions…"
        rows={3}
        style={{
          fontFamily: "Inter, sans-serif", fontSize: 14, color: "#0A0A0A", width: "100%", boxSizing: "border-box",
          border: `1.5px solid ${filled ? "#E6E6E9" : "#FCA5A5"}`, borderRadius: 12, padding: "12px 14px",
          outline: "none", resize: "none", background: "#fff",
        }}
      />
      {!filled && (
        <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#B91C1C" }}>
          Sans cette précision, l'atelier ne sait pas quoi chercher.
        </span>
      )}
    </div>
    );
  });

  // ── drill-in: one category's prestations, priced ──
  if (openCat) {
    const card = NEED_CARDS.find((c) => c.id === openCat);
    const matches = interventions.filter((i) => i.type === openCat && i.active);
    const rows = matches.length > 0
      ? matches.map((i) => ({ id: i.id, name: i.name, minutes: i.estimated_minutes, fast: i.fast }))
      : [{ id: `whole-${openCat}`, name: card?.label || "", minutes: 0, fast: false }];
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Illus name={openCat} size={68} style={{ flexShrink: 0 }} />
          <div>
            <h2 style={{ ...V2_TITLE, fontSize: 22 }}>{card?.label}</h2>
            <p style={{ ...V2_LEDE, marginTop: 4, fontSize: 14 }}>
              {matches.length > 0 ? `${matches.length} prestation${matches.length > 1 ? "s" : ""}` : "Prestation sur devis"}
            </p>
          </div>
        </div>
        {promotions.filter((p) => p.category === openCat).map((p) => <PromoCard key={p.id || p.title} promo={p} />)}
        <div style={{ display: "flex", flexDirection: "column" }}>
          {rows.map((r) => {
            const p = splitPrice(r.name);
            const on = selected.has(r.name);
            return (
              <button key={r.id} className={`bkui-row${on ? " on" : ""}`} onClick={() => toggleIntervention(r.name)}>
                <span className="bkui-tick">{V2_CHECK}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontFamily: "Inter, sans-serif", fontSize: 15, fontWeight: 500, letterSpacing: "-0.01em", lineHeight: 1.3 }}>{p.label}</span>
                  {r.minutes > 0 && (
                    <span style={{ display: "block", fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "#A1A1A6", marginTop: 3 }}>
                      {formatEstimatedDuration(r.minutes)}{r.fast ? " · Service rapide" : ""}
                    </span>
                  )}
                </span>
                <span style={{ marginLeft: "auto", textAlign: "right", flexShrink: 0, paddingLeft: 8 }}>
                  {p.price ? (
                    <>
                      <b style={{ display: "block", fontFamily: "Inter, sans-serif", fontSize: 14.5, fontWeight: 600, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>{p.price} €</b>
                      <span style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "#A1A1A6" }}>{p.from ? "à partir de" : "TTC"}</span>
                    </>
                  ) : (
                    <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13.5, fontWeight: 500, color: "#A1A1A6" }}>Sur devis</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
        {details}
      </div>
    );
  }

  // ── category grid ──
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>
      <div>
        <h2 style={V2_TITLE}>De quel service<br />avez-vous besoin ?</h2>
        <p style={{ ...V2_LEDE, marginTop: 8 }}>Choisissez une catégorie. Plusieurs interventions possibles.</p>
      </div>

      <div className="bkui-grid">
        {NEED_CARDS.map((card, i) => {
          const matches = interventions.filter((x) => x.type === card.id && x.active);
          const count = matches.filter((x) => selected.has(x.name)).length + (selected.has(card.label) ? 1 : 0);
          const promo = promotions.some((p) => p.category === card.id);
          return (
            <button key={card.id} className={`bkui-tile${count > 0 ? " on" : ""}`}
              style={{ animationDelay: `${i * 38}ms`, ...(promo && count === 0 ? { boxShadow: "inset 0 0 0 2px #FFD43B", background: "#FFFBEA" } : {}) }}
              onClick={() => setOpenCat(card.id)}>
              {count > 0 && <span className="bkui-badge">{count}</span>}
              {promo && <span style={PROMO_TAG}>Offre en cours</span>}
              <Illus name={card.id} />
              <span className="bkui-tl">{card.label}</span>
              <span className="bkui-td">{card.desc}</span>
            </button>
          );
        })}

        {/* « Autre » est une tuile comme les autres (cf. widget client) : en ligne
            discrète sous la grille elle passait inaperçue, et elle complète la
            dernière rangée maintenant que les catégories sont en nombre impair. */}
        <button className={`bkui-tile${isAutre ? " on" : ""}`}
          style={{ animationDelay: `${NEED_CARDS.length * 38}ms` }}
          onClick={() => setAutreOpen((o) => !o)}>
          {isAutre && <span className="bkui-mark">{V2_MARK}</span>}
          <Illus name="autre" />
          <span className="bkui-tl">Autre / Je ne sais pas</span>
          <span className="bkui-td">Décrivez le besoin librement</span>
        </button>
      </div>

      {/* Free text — coexists with the picked interventions, unlike the client flow */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {isAutre && (
          <textarea
            ref={autreRef}
            className="bk-reveal"
            value={data.custom_intervention}
            onChange={(e) => update("custom_intervention", e.target.value)}
            placeholder="Décrivez votre besoin..."
            rows={3}
            autoFocus
            style={{
              fontFamily: "Inter, sans-serif", fontSize: 14, color: "#0A0A0A", width: "100%", boxSizing: "border-box",
              border: "1.5px solid #E6E6E9", borderRadius: 12, padding: "12px 14px",
              outline: "none", resize: "none", background: "#fff",
            }}
          />
        )}

        {internalMode && (
          <label
            style={{
              display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer",
              border: `1.5px solid ${data.internal_work ? "#6366F1" : "#E6E6E9"}`,
              borderRadius: 12, padding: "12px 14px", background: data.internal_work ? "#F5F6FF" : "#fff",
              transition: "border-color .15s, background .15s",
            }}
          >
            <input
              type="checkbox"
              checked={data.internal_work}
              onChange={(e) => update("internal_work", e.target.checked)}
              style={{ marginTop: 2, accentColor: "#6366F1", width: 16, height: 16, cursor: "pointer" }}
            />
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 14, fontWeight: 600, color: "#0A0A0A" }}>
                Travaux interne
              </span>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#6B6B70" }}>
                Aucune charge ne sera posée au planning atelier.
              </span>
            </span>
          </label>
        )}

        {internalMode && (
          <label
            style={{
              display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer",
              border: `1.5px solid ${data.arrived_by_assistance ? "#6366F1" : "#E6E6E9"}`,
              borderRadius: 12, padding: "12px 14px", background: data.arrived_by_assistance ? "#F5F6FF" : "#fff",
              transition: "border-color .15s, background .15s",
            }}
          >
            <input
              type="checkbox"
              checked={data.arrived_by_assistance}
              onChange={(e) => update("arrived_by_assistance", e.target.checked)}
              style={{ marginTop: 2, accentColor: "#6366F1", width: 16, height: 16, cursor: "pointer" }}
            />
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 14, fontWeight: 600, color: "#0A0A0A" }}>
                Véhicule arrivé par assistance
              </span>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#6B6B70" }}>
                Mentionné sur l'OR ; le RDV ne sera pas placé automatiquement à l'atelier.
              </span>
            </span>
          </label>
        )}
      </div>

      {data.interventions.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <span style={{ width: "100%", fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "#A1A1A6", marginBottom: 2 }}>
            Sélection
          </span>
          {data.interventions.map((n) => (
            <span key={n} style={{
              display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 10px 8px 13px", borderRadius: 999,
              background: "#F3F3F4", fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 500, letterSpacing: "-0.01em",
            }}>
              {splitPrice(n).label}
              <button onClick={() => toggleIntervention(n)} aria-label="Retirer"
                style={{ border: 0, background: "none", cursor: "pointer", display: "flex", color: "#A1A1A6", padding: 0 }}>
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {details}
    </div>
  );
}

/** Result of the recall-campaign check run at the vehicle step. `plate` is the
 *  normalized plate it belongs to, so a stale result is never shown for another car. */
export type CampaignCheck = {
  plate: string;
  status: "checking" | "found" | "none" | "unavailable";
  campaigns: { code: string; libelle: string }[];
};

/** Shown under the vehicle once "Rappel constructeur" is part of the basket.
 *
 *  A campaign is the one motive we can verify: ServiceBox knows which vehicles are
 *  targeted. When it says none, and the campaign is the only reason for the RDV,
 *  there is nothing to work on — booking it would burn an atelier slot. The message
 *  form is the way out: it reaches the garage by email, and no RDV is created. */
const CAMPAIGN_FIELD_STYLE: React.CSSProperties = {
  fontFamily: "Inter, sans-serif", fontSize: 13, color: "#0A0A0A",
  padding: "10px 12px", borderRadius: 10, border: "1.5px solid #FDE68A",
  background: "#fff", outline: "none", width: "100%", boxSizing: "border-box",
};

/** ServiceBox answers in ~1s warm, but a cold SSO session has been seen at 8s+.
 *  Past this, we stop waiting and treat it as "unavailable": not knowing must never
 *  park someone on a spinner, and it is not a reason to refuse the RDV. */
const CAMPAIGN_CHECK_TIMEOUT_MS = 20000;

function CampaignCheckPanel({ check, campagneOnly, onSend, sent, setSent, defaultName, defaultPhone, defaultEmail }: {
  check: CampaignCheck | null;
  /** Campagne is the only motive → "no campaign" blocks the booking. */
  campagneOnly: boolean;
  onSend: (payload: { nom: string; phone: string; email: string; message: string }) => Promise<void>;
  sent: boolean;
  setSent: (v: boolean) => void;
  /** Known already (internal: the client linked to the plate) — the form starts filled. */
  defaultName?: string;
  defaultPhone?: string;
  defaultEmail?: string;
}) {
  const [message, setMessage] = useState("");
  // This form runs on the vehicle step, before the contact step exists, so it asks
  // for the callback details itself — a request the garage cannot answer is useless.
  const [nom, setNom] = useState(defaultName || "");
  const [phone, setPhone] = useState(defaultPhone || "");
  const [email, setEmail] = useState(defaultEmail || "");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  // The client can be linked after this panel mounted (staff picking the owner from
  // the plate). Keep the untouched form in sync until it is opened.
  useEffect(() => {
    if (open) return;
    setNom(defaultName || "");
    setPhone(defaultPhone || "");
    setEmail(defaultEmail || "");
  }, [open, defaultName, defaultPhone, defaultEmail]);

  if (!check) return null;

  const reachable = phone.replace(/\s/g, "").length >= 10 || /.+@.+\..+/.test(email.trim());
  const canSend = !!message.trim() && !!nom.trim() && reachable;

  const send = async () => {
    if (!canSend || sending) return;
    setSending(true);
    setError(null);
    try {
      await onSend({ nom: nom.trim(), phone: phone.trim(), email: email.trim(), message: message.trim() });
      setSent(true);
    } catch {
      setError("L'envoi a échoué. Réessayez dans un instant.");
    }
    setSending(false);
  };

  if (check.status === "checking") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, background: "#F7F7F7" }}>
        <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid #E5E5E5", borderTopColor: "#000", animation: "cgSpin 0.6s linear infinite", flexShrink: 0 }} data-cg-spinner />
        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#6B7280" }}>Vérification des campagnes de rappel…</span>
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "#A1A1A1" }}>Interrogation du constructeur, cela peut prendre quelques secondes.</span>
        </div>
        {/* Own keyframes name: the page defines `spin` too, and reduced-motion flattens
            every animation under .bkui — here the spin is the only sign of life. */}
        <style>{`@keyframes cgSpin { to { transform: rotate(360deg); } }
          @media (prefers-reduced-motion: reduce) { .bkui [data-cg-spinner] { animation-duration: 0.6s !important; } }`}</style>
      </div>
    );
  }

  if (check.status === "found") {
    return (
      <div className="bk-reveal" style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 14px", borderRadius: 12, background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><path d="M5 13l4 4L19 7" stroke="#16A34A" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
          <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 13, color: "#166534" }}>
            Ce véhicule est bien concerné {check.campaigns.length > 1 ? "par ces campagnes" : "par cette campagne"}
          </span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingLeft: 24 }}>
          {check.campaigns.map((c, i) => (
            <span key={`${c.code}-${i}`} style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "#166534", lineHeight: 1.4 }}>
              {[c.code, c.libelle].filter(Boolean).join(" — ")}
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (check.status === "unavailable") {
    // ServiceBox unreachable or no VIN for this plate — we don't know, so we never
    // claim there is no campaign and we never block on it.
    return (
      <div className="bk-reveal" style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, background: "#F7F7F7" }}>
        <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
        </svg>
        <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "#6B7280", lineHeight: 1.4 }}>
          Les campagnes n'ont pas pu être vérifiées pour ce véhicule. Le rendez-vous reste possible.
        </span>
      </div>
    );
  }

  // status === "none"
  if (sent) {
    return (
      <div className="bk-reveal" style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", borderRadius: 12, background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M5 13l4 4L19 7" stroke="#16A34A" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
        <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "#166534", lineHeight: 1.45 }}>
          Demande envoyée au garage. Elle sera traitée sans rendez-vous : le garage recontacte le client pour
          confirmer la campagne avant de fixer une date.
        </span>
      </div>
    );
  }

  return (
    <div className="bk-reveal" style={{ display: "flex", flexDirection: "column", gap: 10, padding: "12px 14px", borderRadius: 12, background: "#FFFBEB", border: "1px solid #FDE68A" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <svg width={15} height={15} viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
          <path d="M8 1.6 15.2 14.4H0.8L8 1.6Z" stroke="#B45309" strokeWidth={1.4} strokeLinejoin="round" />
          <path d="M8 6.2v3.4" stroke="#B45309" strokeWidth={1.4} strokeLinecap="round" />
          <circle cx={8} cy={11.9} r={0.85} fill="#B45309" />
        </svg>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 13, color: "#92400E" }}>
            Aucune nouvelle campagne n'a été détectée
          </span>
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "#92400E", lineHeight: 1.45 }}>
            {campagneOnly
              ? "Sans autre intervention à réaliser, le rendez-vous ne peut pas être pris. Envoyez une demande au garage : il vérifie et recontacte."
              : "Le rendez-vous reste possible pour les autres interventions sélectionnées."}
          </span>
        </div>
      </div>

      {campagneOnly && !open && (
        <button type="button" onClick={() => setOpen(true)}
          style={{ alignSelf: "flex-start", padding: "9px 14px", borderRadius: 10, border: "1.5px solid #92400E", background: "transparent", fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 12.5, color: "#92400E", cursor: "pointer" }}>
          Envoyer une demande au garage
        </button>
      )}

      {campagneOnly && open && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Nom" style={CAMPAIGN_FIELD_STYLE} />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Téléphone" inputMode="tel" style={CAMPAIGN_FIELD_STYLE} />
          </div>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (facultatif)" inputMode="email" style={CAMPAIGN_FIELD_STYLE} />
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
            rows={4}
            placeholder="Ex : courrier du constructeur reçu le 12/09, référence de la campagne…"
            style={{ ...CAMPAIGN_FIELD_STYLE, lineHeight: 1.45, resize: "vertical" }}
          />
          {error && <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#B91C1C" }}>{error}</span>}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button type="button" onClick={send} disabled={!canSend || sending}
              style={{
                padding: "9px 14px", borderRadius: 10, border: "none",
                background: !canSend || sending ? "#D6D3D1" : "#92400E",
                fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 12.5, color: "#fff",
                cursor: !canSend || sending ? "default" : "pointer",
              }}>
              {sending ? "Envoi…" : "Envoyer"}
            </button>
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "#A16207", flex: 1, minWidth: 180 }}>
              {canSend || sending
                ? "Envoyé par e-mail à l'adresse de contact du garage. Aucun rendez-vous n'est créé."
                : "Nom, téléphone (ou email) et message sont nécessaires pour être rappelé."}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Step 1 — Vehicle (plate input) ─────────────────────────

/** A party that can pay for the RDV — one of the vehicle's propriétaire / société /
 *  conducteur blocks (several roles merged when they are the same dossier). */
type PayerOption = {
  key: string;
  roles: string[];
  nom: string;
  dossier_id?: string;
  type_client?: string;
  telephone?: string;
  email?: string;
  adresse?: string;
};

const PARTY_ROLES: { slot: "owner" | "company" | "user"; label: string }[] = [
  { slot: "owner", label: "Propriétaire" },
  { slot: "company", label: "Société" },
  { slot: "user", label: "Conducteur" },
];

/** Distinct parties a fleet / loueur vehicle is linked to. The vehicle's home dossier
 *  is added when it is none of them, so the current behaviour stays one of the choices. */
function buildPayerOptions(
  veh: Partial<Pick<ClientVehicle, "owner" | "company" | "user">> | null | undefined,
  home: { id?: string; name?: string } | null,
): PayerOption[] {
  const out: PayerOption[] = [];
  const normName = (n: string) => n.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim().toUpperCase();
  const add = (role: string, b: VehiclePartyBlock) => {
    const nom = (b.nom || "").trim();
    if (!nom && !b.dossier_id) return;
    const key = b.dossier_id ? `d:${b.dossier_id}` : `n:${normName(nom)}`;
    const same = out.find((o) => o.key === key);
    if (same) { if (same.roles.indexOf(role) < 0) same.roles.push(role); return; }
    out.push({
      key, roles: [role], nom: nom || "Client", dossier_id: b.dossier_id || undefined,
      type_client: b.type_client || undefined, telephone: b.telephone || undefined,
      email: b.email || undefined,
      adresse: [b.adresse, [b.code_postal, b.ville].filter(Boolean).join(" ")].filter(Boolean).join(", ") || undefined,
    });
  };
  PARTY_ROLES.forEach(({ slot, label }) => { const b = veh?.[slot]; if (b) add(label, b); });
  if (home?.id && !out.some((o) => o.dossier_id === home.id)) {
    out.push({ key: `d:${home.id}`, roles: ["Fiche du véhicule"], nom: home.name || "Client", dossier_id: home.id });
  }
  return out;
}

function StepVehicle({
  data, update, orgId,
  clientQuery, setClientQuery, clientVehicles, setClientVehicles, addingVehicle, setAddingVehicle,
  payerOptions, setPayerOptions, payerKey, setPayerKey,
  campagneSelected, campagneOnly, campaignCheck, campaignRequestSent, setCampaignRequestSent, onSendCampaignRequest,
  prefill, onPrefillUsed,
}: {
  data: BookingData;
  update: (k: keyof BookingData, v: unknown) => void;
  orgId: string;
  clientQuery: string;
  setClientQuery: (v: string) => void;
  clientVehicles: ClientVehicle[];
  setClientVehicles: (v: ClientVehicle[]) => void;
  addingVehicle: boolean;
  setAddingVehicle: (v: boolean) => void;
  /** Fleet / loueur car picked by plate: the parties that can pay, and the one chosen.
   *  Lifted to the page so the choice survives going back to this step. */
  payerOptions: PayerOption[];
  setPayerOptions: (v: PayerOption[]) => void;
  payerKey: string | null;
  setPayerKey: (v: string | null) => void;
  /** "Rappel constructeur" is in the basket -> the vehicle is checked against ServiceBox. */
  campagneSelected: boolean;
  /** ...and it is the only motive, so "no campaign" leaves nothing to work on. */
  campagneOnly: boolean;
  campaignCheck: CampaignCheck | null;
  campaignRequestSent: boolean;
  setCampaignRequestSent: (v: boolean) => void;
  onSendCampaignRequest: (payload: { nom: string; phone: string; email: string; message: string }) => Promise<void>;
  /** Véhicule et client déjà connus (RDV secondaire d'un ordre) : choisis d'office, une fois. */
  prefill?: BookingPrefill | null;
  onPrefillUsed?: () => void;
}) {
  const [lookup, setLookup] = useState<{ found: boolean; marque?: string; modele?: string; image_url?: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Internal: search a client OR a plate (Algolia covers nom / téléphone / immat).
  const [clientResults, setClientResults] = useState<ProjectHit[]>([]);
  const clientTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clientSeqRef = useRef(0);  // guards against out-of-order autocomplete responses
  // Immat autocomplete (Algolia dossiers index) shown under the plate input.
  type PlateHit = { plate: string; marque?: string; modele?: string; dossier_id?: string; client_name?: string };
  const [plateResults, setPlateResults] = useState<PlateHit[]>([]);
  const plateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const plateSeqRef = useRef(0);  // guards against out-of-order autocomplete responses
  // Normalized plate for which we already know the vehicle (picked from a known
  // source) — the identification effect skips the slow proxy lookup for it.
  const knownPlateRef = useRef<string | null>(null);
  const normPlate = (p: string) => p.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  // Filter inside the client's fleet — a big parc is faster to search than to scroll.
  const [vehicleFilter, setVehicleFilter] = useState("");
  // Immat-first on a fleet / loueur car linked to several parties: staff picks who
  // pays (= the client of the RDV). Empty / single → unchanged behaviour.
  const payerSeqRef = useRef(0);  // a newer plate pick / choice supersedes a pending one
  const resetPayers = () => {
    payerSeqRef.current++;
    setPayerOptions([]);
    setPayerKey(null);
    if (data.payer_pending) update("payer_pending", false);
  };

  const searchClients = (query: string) => {
    setClientQuery(query);
    if (clientTimerRef.current) clearTimeout(clientTimerRef.current);
    if (!orgId || query.trim().length < 2) { setClientResults([]); return; }
    clientTimerRef.current = setTimeout(async () => {
      try { setClientResults(await searchDossiersFull(query, orgId)); }
      catch { setClientResults([]); }
    }, 350);
  };

  const searchPlates = (raw: string) => {
    if (plateTimerRef.current) clearTimeout(plateTimerRef.current);
    const q = raw.replace(/[\s-]/g, "");
    if (!orgId || q.length < 2) { setPlateResults([]); return; }
    // Debounce until the user pauses, then ignore any out-of-order (stale) response.
    const seq = ++plateSeqRef.current;
    plateTimerRef.current = setTimeout(async () => {
      try {
        const res = await searchPlatesFull(q, orgId);
        if (seq === plateSeqRef.current) setPlateResults(res);
      } catch { if (seq === plateSeqRef.current) setPlateResults([]); }
    }, 350);
  };

  // Identify a car we already have saved data for — set the ID card directly
  // and mark the plate as known so no ServiceBox proxy lookup fires.
  const setKnownVehicle = (v: { plate: string; marque?: string; modele?: string; image_url?: string }) => {
    knownPlateRef.current = normPlate(v.plate);
    setLookup({ found: true, marque: v.marque, modele: v.modele, image_url: v.image_url });
  };

  // Two INDEPENDENT flows, no cross-triggering:
  //  - client-first (selectClient): prefill client + show its vehicles picker
  //  - immat-first (selectPlate): set the chosen vehicle + link its client,
  //    WITHOUT opening the vehicle picker (the vehicle is already chosen).

  // Every field falls back to the dossier level, because the two are not always in sync:
  // a dossier born from a ServiceBox RDV carries its coordinates on the folder while its
  // contact is empty, and reading the contact alone is what made staff retype a phone
  // number and an address Carvis already held.
  const _prefillContact = (dossier: { id?: string; contacts?: { is_primary?: boolean; nom?: string; prenom?: string; phone?: string; email?: string; adresse?: string }[]; name?: string; phone?: string; email?: string; address?: string }) => {
    update("dossier_id", dossier.id || "");
    const contact = dossier.contacts?.find((c) => c.is_primary) || dossier.contacts?.[0];
    const nom = contact?.nom || (contact ? "" : dossier.name);
    if (nom) update("nom", nom);
    update("prenom", contact?.prenom || "");
    const phone = contact?.phone || dossier.phone;
    if (phone) update("phone", phone);
    const email = contact?.email || dossier.email;
    if (email) update("email", email);
    // Reuse the address already saved on the client — no need to re-ask.
    const adresse = contact?.adresse || dossier.address;
    if (adresse) update("address", adresse);
    update("address_prefilled", !!adresse);
    // A known client is now linked — don't re-search them on the final step.
    update("client_selected", true);
  };

  // Client-first: pick a client → propose its vehicles.
  const selectClient = async (hit: ProjectHit) => {
    setClientResults([]);
    resetPayers();
    setClientQuery(hit.label);
    try {
      const dossier = await dossiersApi.get(hit.value);
      // Changement de propriétaire: the car is already chosen — this client is its
      // NEW owner, so never swap the plate for one of the cars they already have.
      if (data.owner_change && data.immatriculation) {
        setClientVehicles([]);
        setAddingVehicle(true);
        _prefillContact(dossier);
        return;
      }
      const vehicles = (dossier.vehicles || []).filter((v) => v.plate);
      setClientVehicles(vehicles);
      if (vehicles.length === 1) { setKnownVehicle(vehicles[0]); update("immatriculation", vehicles[0].plate); setAddingVehicle(false); }
      else if (vehicles.length === 0) { update("immatriculation", ""); setAddingVehicle(true); }
      else { update("immatriculation", ""); setAddingVehicle(false); }  // show the picker
      _prefillContact(dossier);
    } catch { /* staff can fill manually */ }
  };

  // Immat-first: the vehicle is chosen — just link its client. No picker.
  const selectPlate = async (hit: PlateHit) => {
    setPlateResults([]);
    resetPayers();
    const seq = payerSeqRef.current;
    update("owner_change", false);
    // Known car → identify it from saved data instantly, skip the proxy lookup.
    setKnownVehicle(hit);
    update("immatriculation", hit.plate);
    setClientVehicles([]);     // keep the chosen vehicle — don't open the picker
    setAddingVehicle(true);
    if (hit.client_name && !data.nom) setClientQuery(hit.client_name);
    if (hit.dossier_id) {
      try {
        const dossier = await dossiersApi.get(hit.dossier_id);
        if (seq !== payerSeqRef.current) return;
        // Refine the ID card with the exact saved vehicle (image, precise marque/modèle).
        const veh = (dossier.vehicles || []).find((v) => v.plate && normPlate(v.plate) === normPlate(hit.plate));
        if (veh) setKnownVehicle(veh);
        // Fleet / loueur car: the party blocks (propriétaire / société / conducteur)
        // live on the canonical client_vehicle — overlaid on the dossier copy, else
        // read from client_vehicles directly.
        let parties: Partial<Pick<ClientVehicle, "owner" | "company" | "user">> | null =
          veh && (veh.owner || veh.company || veh.user) ? veh : null;
        if (!parties) {
          try {
            const found = await clientVehiclesApi.search({ plate: hit.plate, org_id: orgId, limit: 5 });
            parties = found.find((v) => normPlate(v.plate || "") === normPlate(hit.plate)) || null;
          } catch { parties = null; }
          if (seq !== payerSeqRef.current) return;
        }
        const options = buildPayerOptions(parties, dossier);
        if (options.length >= 2) {
          // Several parties → nobody is linked until staff says who pays.
          setPayerOptions(options);
          setClientQuery("");
          update("dossier_id", "");
          update("nom", ""); update("prenom", ""); update("phone", ""); update("email", "");
          update("address", ""); update("address_prefilled", false);
          update("client_selected", false);
          update("payer_pending", true);
          return;
        }
        _prefillContact(dossier);
      } catch { /* staff can fill manually */ }
    }
  };

  // Fleet / loueur car: staff picks who pays → that party becomes the RDV client.
  const choosePayer = async (opt: PayerOption) => {
    const seq = ++payerSeqRef.current;
    setPayerKey(opt.key);
    update("payer_pending", false);
    update("owner_change", false);
    update("prenom", "");
    if (opt.dossier_id) {
      try {
        const dossier = await dossiersApi.get(opt.dossier_id);
        if (seq !== payerSeqRef.current) return;
        // Reset first: _prefillContact only overwrites the fields the dossier has.
        update("phone", ""); update("email", ""); update("address", "");
        _prefillContact(dossier);
        return;
      } catch { if (seq !== payerSeqRef.current) return; /* dossier gone → snapshot below */ }
    }
    // No dossier behind this party: use its carte-grise snapshot; the contact step
    // still recognises an existing client by phone (client_selected stays false).
    update("dossier_id", "");
    update("nom", opt.nom);
    update("phone", opt.telephone || "");
    update("email", opt.email || "");
    update("address", opt.adresse || "");
    update("address_prefilled", false);
    update("client_selected", false);
  };

  // Reset both the vehicle and the linked client (e.g. start over).
  const changeOwner = () => {
    resetPayers();
    setClientVehicles([]);
    setClientQuery("");
    setAddingVehicle(true);
    update("immatriculation", "");
    update("nom", ""); update("prenom", ""); update("phone", ""); update("email", "");
    update("client_selected", false);
    update("owner_change", false);
    update("dossier_id", "");
  };

  // Immat-first: keep the chosen plate but unlink the client (changement de
  // propriétaire) so staff can search the new owner via the top search box.
  const clearLinkedClient = () => {
    resetPayers();
    setClientQuery("");
    update("dossier_id", "");
    update("nom", ""); update("prenom", ""); update("phone", ""); update("email", "");
    update("address", ""); update("address_prefilled", false);
    update("client_selected", false);
    update("owner_change", true);
  };

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const plate = data.immatriculation.replace(/[\s-]/g, "");
    if (plate.length < 7) { setLookup(null); knownPlateRef.current = null; return; }
    // Already identified from saved data (client vehicle / plate autocomplete) — no proxy call.
    if (knownPlateRef.current === plate.toUpperCase()) return;
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await bookingsApi.lookupVehicle(data.immatriculation, orgId);
        setLookup(result);
      } catch { setLookup(null); }
      setLoading(false);
    }, 400);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [data.immatriculation, orgId]);

  const showPicker = clientVehicles.length > 0 && !addingVehicle;
  // A new client (or a reset) starts from an unfiltered fleet.
  useEffect(() => { setVehicleFilter(""); }, [clientVehicles]);

  // RDV secondaire : même chemin que si l'accueil avait choisi la plaque dans la recherche.
  useEffect(() => {
    if (!prefill) return;
    onPrefillUsed?.();
    selectPlate(prefill);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const searchableFleet = clientVehicles.length > 3;
  const filteredVehicles = useMemo(() => {
    const q = vehicleFilter.trim();
    if (!q) return clientVehicles;
    const norm = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
    const nq = norm(q);
    const pq = normPlate(q);
    return clientVehicles.filter((v) =>
      norm([v.marque, v.modele, v.plate].filter(Boolean).join(" ")).includes(nq) ||
      (!!pq && normPlate(v.plate).includes(pq)),
    );
  }, [clientVehicles, vehicleFilter]);
  // Never show more than 6 cards — the rest is reached through the filter.
  const visibleVehicles = filteredVehicles.slice(0, 6);
  const hiddenVehicles = filteredVehicles.length - visibleVehicles.length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, flex: 1 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 26, lineHeight: 1.12, letterSpacing: "-0.032em", color: "#0A0A0A", margin: 0 }}>
          Quel est votre véhicule ?
        </h2>
        <p style={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: "#A1A1A1", margin: 0 }}>
          Recherchez le client ou l'immatriculation, ou saisissez la plaque.
        </p>
      </div>

      {/* Internal: client / plate search */}
      <div style={{ position: "relative" }}>
        <input
          className="bk-input"
          type="text"
          value={clientQuery}
          onChange={(e) => searchClients(e.target.value)}
          placeholder="Rechercher un client (nom, téléphone, immatriculation)…"
          style={{
            fontFamily: "Inter, sans-serif", fontSize: 14, color: "#000", width: "100%",
            border: "1.5px solid #E5E5E5", borderRadius: 12, padding: "10px 14px",
            outline: "none", background: "#fff",
          }}
        />
        {clientResults.length > 0 && (
          <div style={{ position: "absolute", left: 0, right: 0, top: "100%", zIndex: 30, marginTop: 4, background: "#fff", border: "1px solid #E5E5E5", borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.10)", maxHeight: 240, overflowY: "auto" }}>
            {clientResults.map((hit) => (
              <button key={hit.value} type="button" onClick={() => selectClient(hit)}
                style={{ display: "block", width: "100%", minHeight: 52, textAlign: "left", padding: "12px 14px", background: "transparent", border: "none", borderBottom: "1px solid #F0F0F0", cursor: "pointer" }}>
                <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 14, color: "#000" }}>{hit.label}</div>
                {(hit.extra_label || hit.extra_label_2) && (
                  <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#A1A1A1", marginTop: 2 }}>{[hit.extra_label, hit.extra_label_2].filter(Boolean).join(" · ")}</div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Vehicle picker (client's vehicles) */}
      {showPicker && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#A1A1A1" }}>
            {clientVehicles.length > 1 ? `Véhicules du client (${clientVehicles.length})` : "Véhicule du client"}
          </span>
          {searchableFleet && (
            <input
              className="bk-input"
              type="text"
              value={vehicleFilter}
              onChange={(e) => setVehicleFilter(e.target.value)}
              placeholder="Filtrer (immatriculation, marque, modèle)…"
              style={{
                fontFamily: "Inter, sans-serif", fontSize: 13, color: "#000", width: "100%",
                border: "1.5px solid #E5E5E5", borderRadius: 12, padding: "9px 14px",
                outline: "none", background: "#fff",
              }}
            />
          )}
          {visibleVehicles.map((v) => {
            const selected = v.plate.replace(/[\s-]/g, "").toUpperCase() === data.immatriculation.replace(/[\s-]/g, "").toUpperCase();
            return (
              <button key={v.id || v.plate} type="button" onClick={() => { setKnownVehicle(v); update("immatriculation", v.plate); }}
                style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 12, width: "100%", textAlign: "left", padding: "12px 14px", borderRadius: 12, background: selected ? "#F5F5F5" : "#FAFAFA", border: `1.5px solid ${selected ? "#000" : "#E5E5E5"}`, cursor: "pointer" }}>
                <div style={{ height: 44, flexShrink: 0, display: "flex", alignItems: "center" }}>
                  <VehicleImage marque={v.marque} modele={v.modele} coloris={v.coloris} mec={v.mec} className="h-[44px]" iconSize={22} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 14, color: "#000" }}>{[v.marque, v.modele].filter(Boolean).join(" ") || "Véhicule"}</span>
                  <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#A1A1A1" }}>{v.plate}</span>
                </div>
              </button>
            );
          })}
          {filteredVehicles.length === 0 && (
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#A1A1A1", padding: "4px 2px" }}>
              Aucun véhicule ne correspond à « {vehicleFilter.trim()} ».
            </span>
          )}
          {hiddenVehicles > 0 && (
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#A1A1A1", padding: "2px" }}>
              +{hiddenVehicles} autre{hiddenVehicles > 1 ? "s" : ""} véhicule{hiddenVehicles > 1 ? "s" : ""} — affinez la recherche pour le{hiddenVehicles > 1 ? "s" : ""} voir.
            </span>
          )}
          <button type="button" onClick={() => { update("immatriculation", ""); setAddingVehicle(true); }}
            style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: "1.5px dashed #D4D4D4", background: "transparent", fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 13, color: "#000", cursor: "pointer" }}>
            + Ajouter un véhicule
          </button>
          <button type="button" onClick={changeOwner}
            style={{ alignSelf: "flex-start", background: "none", border: "none", padding: 0, fontFamily: "Inter, sans-serif", fontSize: 12, color: "#A1A1A1", textDecoration: "underline", cursor: "pointer" }}>
            Le véhicule a changé de propriétaire
          </button>
        </div>
      )}

      {/* Plate input (manual / add-vehicle path) */}
      <div style={{ display: showPicker ? "none" : "flex", flexDirection: "column", gap: 12 }}>
        {clientVehicles.length > 0 && (
          <button type="button" onClick={() => { update("immatriculation", ""); setAddingVehicle(false); }}
            style={{ alignSelf: "flex-start", background: "none", border: "none", padding: 0, fontFamily: "Inter, sans-serif", fontSize: 13, color: "#000", textDecoration: "underline", cursor: "pointer" }}>
            ← Choisir un véhicule du client
          </button>
        )}
        {/* Immat-first, fleet / loueur car linked to several parties: who pays? */}
        {clientVehicles.length === 0 && payerOptions.length >= 2 && data.immatriculation && (
          <div className="bk-reveal" style={{ display: "flex", flexDirection: "column", gap: 8, padding: 12, borderRadius: 12, background: "#F7F7F7", border: data.payer_pending ? "1.5px solid #000" : "1.5px solid transparent" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 14, color: "#000" }}>Qui paie ?</span>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#6B7280" }}>
                Ce véhicule est lié à plusieurs clients — celui choisi devient le client du RDV.
              </span>
            </div>
            {payerOptions.map((opt) => {
              const selected = payerKey === opt.key;
              const sub = [opt.type_client, opt.telephone, opt.dossier_id ? "" : "sans fiche client"].filter(Boolean).join(" · ");
              return (
                <button key={opt.key} type="button" onClick={() => choosePayer(opt)} aria-pressed={selected}
                  style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 10, width: "100%", minHeight: 52, textAlign: "left", padding: "10px 12px", borderRadius: 10, background: "#fff", border: `1.5px solid ${selected ? "#000" : "#E5E5E5"}`, cursor: "pointer" }}>
                  <span aria-hidden="true" style={{ width: 16, height: 16, flexShrink: 0, borderRadius: "50%", border: `1.5px solid ${selected ? "#000" : "#C4C4C4"}`, boxShadow: selected ? "inset 0 0 0 3px #fff" : "none", background: selected ? "#000" : "#fff" }} />
                  <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
                    <span style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                      <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 13, color: "#000", overflowWrap: "anywhere" }}>{opt.nom}</span>
                      {opt.roles.map((r) => (
                        <span key={r} style={{ fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 500, color: "#374151", background: "#EEEEEE", borderRadius: 6, padding: "1px 6px" }}>{r}</span>
                      ))}
                    </span>
                    {sub && <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#A1A1A1" }}>{sub}</span>}
                  </span>
                </button>
              );
            })}
            {!data.payer_pending && (
              <button type="button" onClick={clearLinkedClient}
                style={{ alignSelf: "flex-start", background: "none", border: "none", padding: 0, fontFamily: "Inter, sans-serif", fontSize: 12, color: "#A1A1A1", textDecoration: "underline", cursor: "pointer" }}>
                Changement de propriétaire
              </button>
            )}
          </div>
        )}
        {/* Immat-first: client linked from the chosen plate */}
        {clientVehicles.length === 0 && payerOptions.length < 2 && data.nom && data.immatriculation && (
          <div className="flex flex-row items-center gap-3 p-2.5 rounded-lg bk-reveal" style={{ background: "#F7F7F7" }}>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "#6B7280" }}>Client lié</span>
              <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 13, color: "#000" }}>{[data.prenom, data.nom].filter(Boolean).join(" ")}</span>
            </div>
            <button type="button" onClick={clearLinkedClient}
              style={{ background: "none", border: "none", padding: 0, fontFamily: "Inter, sans-serif", fontSize: 12, color: "#A1A1A1", textDecoration: "underline", cursor: "pointer" }}>
              Changement de propriétaire
            </button>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 6, position: "relative" }}>
          <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: 13, color: "#6B6B70" }}>Immatriculation</span>
          <div className="bkui-plate">
            <span className="bkui-plate-eu">
              <svg width={17} height={17} viewBox="0 0 24 24" aria-hidden="true">
                {Array.from({ length: 12 }).map((_, i) => {
                  const a = (i * Math.PI) / 6;
                  return <circle key={i} cx={12 + 7.4 * Math.sin(a)} cy={12 - 7.4 * Math.cos(a)} r={1.25} fill="#FFCC00" />;
                })}
              </svg>
              F
            </span>
            <input
              type="text"
              value={data.immatriculation}
              onChange={(e) => {
                update("immatriculation", formatPlateV2(e.target.value));
                if (data.owner_change) update("owner_change", false);  // another car: not the one changing hands
                if (payerOptions.length) resetPayers();  // another car: its parties no longer apply
                searchPlates(e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase());
              }}
              placeholder="AB-123-CD"
              autoFocus
            />
          </div>
          {/* Immat autocomplete (Algolia) — pick a plate to prefill its linked client */}
          {plateResults.length > 0 && (
            <div style={{ position: "absolute", left: 0, right: 0, top: "100%", zIndex: 30, marginTop: 4, background: "#fff", border: "1px solid #E5E5E5", borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.10)", maxHeight: 240, overflowY: "auto" }}>
              {plateResults.map((hit, i) => (
                <button key={`${hit.plate}-${i}`} type="button" onClick={() => selectPlate(hit)}
                  style={{ display: "block", width: "100%", minHeight: 52, textAlign: "left", padding: "12px 14px", background: "transparent", border: "none", borderBottom: "1px solid #F0F0F0", cursor: "pointer" }}>
                  <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 14, color: "#000" }}>
                    {hit.plate}{hit.client_name ? ` — ${hit.client_name}` : ""}
                  </div>
                  {(hit.marque || hit.modele) && (
                    <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#A1A1A1", marginTop: 2 }}>{[hit.marque, hit.modele].filter(Boolean).join(" ")}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Vehicle result */}
        <div style={{ minHeight: 70, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {loading && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid #E5E5E5", borderTopColor: "#000", animation: "spin 0.6s linear infinite" }} />
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#A1A1A1" }}>Identification...</span>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}
          {!loading && lookup?.found && (
            <div style={{
              display: "flex", alignItems: "center", gap: 14, width: "100%",
              padding: "14px 16px", borderRadius: 14, background: "#F5F5F5", border: "1px solid #E5E5E5",
            }}>
              {lookup.image_url && <img src={lookup.image_url} alt="" style={{ height: 52, objectFit: "contain" }} />}
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 15, color: "#000" }}>
                  {lookup.marque} {lookup.modele}
                </span>
                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#A1A1A1" }}>
                  {data.immatriculation}
                </span>
              </div>
            </div>
          )}
          {!loading && lookup && !lookup.found && data.immatriculation.replace(/[\s-]/g, "").length >= 7 && (
            <div className="bk-reveal" style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "12px 14px", borderRadius: 12, background: "#F7F7F7" }}>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#6B7280" }}>
                Véhicule non reconnu — vous pouvez continuer
              </span>
            </div>
          )}
        </div>

      </div>

      {/* Kilométrage — HORS du bloc « saisie plaque », qui est masqué dès que le
          client a des véhicules : le champ devenait alors introuvable alors qu'il
          reste obligatoire pour continuer, et la prise de RDV était bloquée. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: 13, color: "#666" }}>
          Kilométrage <span style={{ fontWeight: 400, color: "#A1A1A1" }}>(exact ou approximatif)</span>
        </span>
        <div className="bk-field" style={{
          display: "flex", alignItems: "center", gap: 10, padding: "14px 16px",
          border: "1.5px solid #E5E5E5", borderRadius: 14, background: "#FAFAFA",
        }}>
          <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" /><path d="M12 12l4-2.5" /><path d="M12 7v1M7 12h1M17 12h-1" />
          </svg>
          <input
            type="text"
            inputMode="numeric"
            value={data.km}
            onChange={(e) => {
              const digits = e.target.value.replace(/[^0-9]/g, "").slice(0, 7);
              update("km", digits ? Number(digits).toLocaleString("fr-FR") : "");
            }}
            placeholder="Ex : 85 000"
            style={{
              fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 18,
              color: "#000", border: "none", outline: "none", background: "transparent",
              flex: 1, minWidth: 0, width: "100%",
            }}
          />
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: "#A1A1A1", flexShrink: 0 }}>km</span>
        </div>
      </div>

      {/* Recall campaign — outside the plate block, which the vehicle picker hides:
          the verdict must show whichever way the car was identified. */}
      {campagneSelected && (
        <CampaignCheckPanel
          check={campaignCheck}
          campagneOnly={campagneOnly}
          onSend={onSendCampaignRequest}
          sent={campaignRequestSent}
          setSent={setCampaignRequestSent}
          defaultName={[data.prenom, data.nom].filter(Boolean).join(" ")}
          defaultPhone={data.phone}
          defaultEmail={data.email}
        />
      )}
    </div>
  );
}

// ─── Step 2 — Date & Time ───────────────────────────────────

export function StepDateTime({
  data, update, orgId, minDaysAhead, maxDaysAhead, isFast, isCampagne, srMinHoursAhead, estimatedMinutes, onEstimatedReadyTimes, urgent,
}: {
  data: BookingData;
  update: (k: keyof BookingData, v: unknown) => void;
  orgId: string;
  minDaysAhead: number;
  maxDaysAhead: number;
  isFast: boolean;
  /** Campagne-only basket → availability comes from the campagnes atelier zone. */
  isCampagne?: boolean;
  srMinHoursAhead: number;
  estimatedMinutes: number;
  onEstimatedReadyTimes?: (times: Record<string, string>) => void;
  /** Manager urgency override → bypass atelier capacity in availability. */
  urgent?: boolean;
}) {
  const { morning: MORNING_SLOTS, afternoon: AFTERNOON_SLOTS, all: ALL_SLOTS } = useSlotGrid();
  const [fullDays, setFullDays] = useState<string[] | null>(null);
  const [unavailable, setUnavailable] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  // True while an auto-selected date is still being validated (its real availability not yet confirmed).
  // We never surface a selected date to the user until we know it actually has open slots.
  const [autoValidating, setAutoValidating] = useState(false);

  const { minDate, cutoffSlots } = useMemo(() => {
    if (isFast) {
      const now = new Date();
      const cutoff = new Date(now.getTime() + srMinHoursAhead * 60 * 60 * 1000);
      const cutoffDate = toLocalDateStr(cutoff);
      const cutoffMinutes = cutoff.getHours() * 60 + cutoff.getMinutes();
      const disabled = ALL_SLOTS.filter((s) => { const [h, m] = s.split(":").map(Number); return h * 60 + m < cutoffMinutes; });
      return { minDate: cutoffDate, cutoffSlots: disabled };
    }
    const d = new Date(); d.setDate(d.getDate() + minDaysAhead);
    return { minDate: toLocalDateStr(d), cutoffSlots: [] as string[] };
  }, [minDaysAhead, isFast, srMinHoursAhead]);

  const maxDate = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + maxDaysAhead);
    return toLocalDateStr(d);
  }, [maxDaysAhead]);

  const currentMonthStr = useMemo(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }, []);
  const viewedMonthRef = useRef(currentMonthStr);
  const fetchFullDays = (month: string) => {
    if (!orgId) return;
    viewedMonthRef.current = month;
    bookingsApi.getFullDays(month, orgId, isFast || undefined, data.service || DEFAULT_SERVICE, estimatedMinutes || undefined, urgent || undefined, isCampagne || undefined)
      .then((res) => setFullDays((prev) => [...(prev || []).filter((d) => !d.startsWith(month)), ...res.full_days]))
      .catch(() => setFullDays((prev) => prev ?? []));
  };

  // Fetch full days on mount / when params change. Only clear date when params actually change.
  const lastFetchKeyRef = useRef("");
  useEffect(() => {
    const key = `${orgId}_${isFast}_${isCampagne}_${data.service}_${estimatedMinutes}`;
    const changed = key !== lastFetchKeyRef.current;
    lastFetchKeyRef.current = key;
    if (changed) {
      setFullDays(null); update("deposit_date", ""); update("deposit_time", "");
    }
    fetchFullDays(currentMonthStr);
    const viewedMonth = viewedMonthRef.current;
    if (viewedMonth !== currentMonthStr) fetchFullDays(viewedMonth);
  }, [orgId, isFast, isCampagne, data.service, estimatedMinutes, urgent]);

  // Auto-select first non-full weekday when no date is chosen yet.
  // The picked date stays "validating" (hidden from the UI as selected) until the availability
  // call below confirms it has real open slots — so a date is never shown selected then struck out.
  useEffect(() => {
    if (data.deposit_date || fullDays === null) return;
    const fullSet = new Set(fullDays);
    const d = new Date(minDate + "T12:00:00");
    const max = new Date(maxDate + "T12:00:00");
    for (let i = 0; i < 60; i++) {
      if (d > max) break;
      const ds = toLocalDateStr(d);
      if (d.getDay() !== 0 && d.getDay() !== 6 && !fullSet.has(ds)) {
        setAutoValidating(true);
        update("deposit_date", ds);
        return;
      }
      d.setDate(d.getDate() + 1);
    }
    // No candidate day found — stop showing the "searching" state.
    setAutoValidating(false);
  }, [fullDays]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch availability when a date is selected (auto or manual)
  useEffect(() => {
    if (!data.deposit_date || !orgId) { setUnavailable([]); return; }
    setLoadingSlots(true);
    const selectedDate = data.deposit_date;
    bookingsApi.getAvailability(selectedDate, data.service || DEFAULT_SERVICE, orgId, isFast || undefined, estimatedMinutes || undefined, urgent || undefined, isCampagne || undefined)
      .then((res) => {
        if (selectedDate !== data.deposit_date) return;
        const taken = new Set(res.unavailable_slots);
        // Le délai mini du Service Rapide retire les heures déjà passées du premier
        // jour réservable. Elles n'étaient retirées qu'à l'affichage : le jour comptait
        // donc comme disponible, était proposé, et sa grille horaire s'affichait vide.
        if (isFast && selectedDate === minDate) cutoffSlots.forEach((s) => taken.add(s));
        const allSlots = ALL_SLOTS.filter((s) => !taken.has(s));
        // Aucun créneau : le jour est barré dans le calendrier et l'auto-sélection
        // repart sur le suivant. Vaut aussi pour un jour choisi à la main — le
        // calendrier se corrige au lieu de laisser sur une impasse.
        if (allSlots.length === 0) {
          setFullDays((prev) => [...(prev || []), selectedDate]);
          update("deposit_date", ""); update("deposit_time", "");
          setAutoValidating(true);
          return;
        }
        setUnavailable(res.unavailable_slots);
        onEstimatedReadyTimes?.(res.estimated_ready_times || {});
        if (allSlots.length > 0 && (!data.deposit_time || !allSlots.includes(data.deposit_time))) {
          update("deposit_time", allSlots[0]);
        }
        // Confirmed: the date now has real availability and can be revealed as selected.
        setAutoValidating(false);
      })
      .catch(() => { setUnavailable([]); setAutoValidating(false); })
      .finally(() => setLoadingSlots(false));
  }, [data.deposit_date, data.service, orgId, isFast, urgent]); // eslint-disable-line react-hooks/exhaustive-deps

  const availableSlots = useMemo(() => {
    const allUnavailable = isFast && data.deposit_date === minDate
      ? new Set([...unavailable, ...cutoffSlots])
      : new Set(unavailable);
    return {
      morning: MORNING_SLOTS.filter((s) => !allUnavailable.has(s)),
      afternoon: AFTERNOON_SLOTS.filter((s) => !allUnavailable.has(s)),
    };
  }, [unavailable, cutoffSlots, isFast, data.deposit_date, minDate]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Illus name="calendrier" size={68} style={{ flexShrink: 0 }} />
        <div>
          <h2 style={{ ...V2_TITLE, fontSize: 22 }}>Quand&nbsp;?</h2>
          <p style={{ ...V2_LEDE, marginTop: 4, fontSize: 14 }}>
            {estimatedMinutes > 0
              ? `Immobilisation estimée : ${formatEstimatedDuration(estimatedMinutes)}.`
              : "Choisissez le jour et l'heure de dépôt."}
          </p>
        </div>
      </div>

      {fullDays === null ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "32px 0" }}>
          <div style={{ width: 28, height: 28, borderRadius: "50%", border: "3px solid #E5E5E5", borderTopColor: "#000", animation: "spin 0.7s linear infinite" }} />
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#A1A1A1" }}>Chargement des disponibilités...</span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <MiniCalendar
          selectedDate={autoValidating ? null : data.deposit_date}
          onSelect={(d) => { setAutoValidating(false); update("deposit_date", d); update("deposit_time", ""); }}
          minDate={minDate}
          maxDate={maxDate}
          fullDays={fullDays}
          onMonthChange={fetchFullDays}
        />
      </div>
      )}

      {/* While auto-selecting, don't reveal any date/slot until availability is confirmed */}
      {autoValidating && fullDays !== null && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 16 }}>
          <div style={{ width: 18, height: 18, borderRadius: "50%", border: "2px solid #E5E5E5", borderTopColor: "#000", animation: "spin 0.6s linear infinite" }} />
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#A1A1A1" }}>Recherche du premier créneau disponible…</span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Time slots — grid layout, grouped by morning / afternoon.
          While re-fetching availability the previous grid stays visible but dimmed,
          so the layout doesn't jump between date changes. */}
      {data.deposit_date && !autoValidating && (
        <div style={{
          display: "flex", flexDirection: "column", gap: 14,
          opacity: loadingSlots ? 0.4 : 1, pointerEvents: loadingSlots ? "none" : "auto",
          transition: "opacity 0.2s ease",
        }}>
          {([["Matin", availableSlots.morning], ["Après-midi", availableSlots.afternoon]] as const).map(([label, slots]) => (
            slots.length > 0 && (
              <div key={label} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 12, letterSpacing: "0.07em", textTransform: "uppercase", color: "#A1A1A6" }}>{label}</span>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(74px, 1fr))", gap: 8 }}>
                  {slots.map((slot) => {
                    const isSelected = data.deposit_time === slot;
                    return (
                      <button key={slot} onClick={() => update("deposit_time", slot)}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "center",
                          padding: "11px 6px", borderRadius: 10,
                          border: isSelected ? "1.5px solid #000" : "1.5px solid #E5E5E5",
                          background: isSelected ? "#000" : "#fff",
                          cursor: "pointer", fontFamily: "Inter, sans-serif", fontSize: 14, fontWeight: 500,
                          color: isSelected ? "#fff" : "#000",
                        }}>
                        {formatSlot(slot)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )
          ))}
          {availableSlots.morning.length === 0 && availableSlots.afternoon.length === 0 && !loadingSlots && (
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#6B7280", textAlign: "center", padding: 16 }}>Aucun créneau disponible ce jour</span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Step 3 — Recap ─────────────────────────────────────────

/** Exported so the availability debug screen can show the very same mobility /
 *  restitution steps the booking flow shows — no second implementation to drift. */
export function StepRecap({
  section, data, update, estimatedMinutes, safetyMarginHours, isDiagnostic, restitutionMarginDays, estimatedReadyTimes, orgConfig, force = false,
}: {
  /** "mobility" = the mobility-solution choice; "restitution" = date/heure de récupération; "recap" = read-only review. */
  section: "mobility" | "restitution" | "recap";
  data: BookingData;
  update: (k: keyof BookingData, v: unknown) => void;
  estimatedMinutes: number;
  safetyMarginHours: number;
  isDiagnostic: boolean;
  restitutionMarginDays: number;
  estimatedReadyTimes: Record<string, string>;
  orgConfig: OrgConfig;
  /** Manager "Urgence — forcer l'atelier". Forcing the drop-off used to leave the
   *  restitution governed by the shop's rules, so an urgent diagnostic could be taken in
   *  today and still not be handed back before the margin allowed. When it is on, the
   *  immobilisation margin, the full days and the busy reception slots all become
   *  advisory — same-day handover included. The only floor left is physical: the car
   *  cannot leave before it arrived. */
  force?: boolean;
}) {
  const ALL_SLOTS = useSlotGrid().all;
  const orgId = useDataStore((s) => s.orgData)?.id ?? "";
  const mobilityOptions = resolveMobilityOptions(orgConfig.booking_mobility_options);
  // Internal: list available loaner/rental vehicles so staff can pick one.
  const _USAGE: Record<string, string> = { pret: "pret", location: "location", velo: "velos" };
  const loanerUsage = _USAGE[data.mobility_option];
  // Fetch every fleet vehicle free over the whole loan window (deposit → restitution) once,
  // then derive availability per usage so we can grey out mobility options that have no free
  // vehicle (rather than only telling staff after they've already picked the option).
  const [availVehicles, setAvailVehicles] = useState<{ id: string; name?: string; plate?: string; marque?: string; modele?: string; usage?: string }[]>([]);
  const [availLoaded, setAvailLoaded] = useState(false);
  useEffect(() => {
    // Loaner availability is only needed on the dedicated mobility step.
    if (section !== "mobility" || !orgId || !data.deposit_date) { setAvailVehicles([]); setAvailLoaded(false); return; }
    let cancelled = false;
    setAvailLoaded(false);
    vehiclesApi.available({
      org_id: orgId,
      start_date: data.deposit_date,
      end_date: data.restitution_date || data.deposit_date,
    })
      .then((vs) => { if (!cancelled) setAvailVehicles(vs as never); })
      .catch(() => { if (!cancelled) setAvailVehicles([]); })
      .finally(() => { if (!cancelled) setAvailLoaded(true); });
    return () => { cancelled = true; };
  }, [section, orgId, data.deposit_date, data.restitution_date]);

  const loaners = useMemo(
    () => (loanerUsage ? availVehicles.filter((v) => v.usage === loanerUsage) : []),
    [availVehicles, loanerUsage],
  );
  // Loaner picker is collapsed by default so the (potentially long) list doesn't
  // take over the recap; staff expand it only to override the auto-assignment.
  const [loanerOpen, setLoanerOpen] = useState(false);
  const selectedLoaner = loaners.find((v) => v.id === data.rental_vehicle_id);
  const selectedLoanerLabel = selectedLoaner
    ? ([selectedLoaner.name || [selectedLoaner.marque, selectedLoaner.modele].filter(Boolean).join(" "), selectedLoaner.plate].filter(Boolean).join(" · ") || "Véhicule")
    : "Automatique (premier disponible)";
  // Only flag an option as unavailable once availability for the current dates has actually
  // loaded — otherwise we'd briefly grey everything out while the fetch is in flight.
  const mobilityUnavailable = useMemo<Record<string, boolean>>(
    () => ({
      velo: !!data.deposit_date && availLoaded && !availVehicles.some((v) => v.usage === "velos"),
      pret: !!data.deposit_date && availLoaded && !availVehicles.some((v) => v.usage === "pret"),
      location: !!data.deposit_date && availLoaded && !availVehicles.some((v) => v.usage === "location"),
    }),
    [availVehicles, availLoaded, data.deposit_date],
  );

  // ── Restitution availability ─────────────────────────────────────────────
  // The handover is done by the same reception desk as the drop-off, so a date or
  // an hour can only be offered when a desk is really free. This step used to
  // propose every weekday and every slot: days where no reception was available
  // were bookable, and the restitution landed on an already-busy column.
  const [restFullDays, setRestFullDays] = useState<string[] | null>(null);
  const [restUnavailable, setRestUnavailable] = useState<string[]>([]);
  const [restSlotsLoading, setRestSlotsLoading] = useState(false);
  const restUnavailableSet = useMemo(() => new Set(restUnavailable), [restUnavailable]);

  const fetchRestFullDays = (month: string) => {
    if (!orgId || !month || !data.deposit_date) return;
    bookingsApi.getRestitutionFullDays(month, orgId, data.deposit_date, data.deposit_time, data.service)
      .then((res) => setRestFullDays((prev) => [...(prev || []).filter((d) => !d.startsWith(month)), ...res.full_days]))
      .catch(() => setRestFullDays((prev) => prev ?? []));
  };

  useEffect(() => {
    if (section !== "restitution" || !orgId || !data.deposit_date) return;
    setRestFullDays(null);
    fetchRestFullDays((data.restitution_date || data.deposit_date).slice(0, 7));
  }, [section, orgId, data.deposit_date, data.deposit_time, data.service]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (section !== "restitution" || !orgId || !data.restitution_date) { setRestUnavailable([]); return; }
    const target = data.restitution_date;
    setRestSlotsLoading(true);
    bookingsApi.getRestitutionAvailability(target, orgId, data.deposit_date, data.deposit_time, data.service)
      .then((res) => { if (target === data.restitution_date) setRestUnavailable(res.unavailable_slots || []); })
      .catch(() => setRestUnavailable([]))
      .finally(() => setRestSlotsLoading(false));
  }, [section, orgId, data.restitution_date, data.deposit_date, data.deposit_time, data.service]); // eslint-disable-line react-hooks/exhaustive-deps

  // Diagnostic always requires leaving the vehicle — no "wait on site" option
  const isShort = !isDiagnostic && estimatedMinutes > 0 && estimatedMinutes < SHORT_THRESHOLD;
  const totalMinutes = estimatedMinutes + safetyMarginHours * 60;
  // Number of business days the vehicle must stay. Driven by the configurable per-intervention
  // delay (min_days_margin, e.g. 2 days for a diagnostic), the estimated duration, and a 1-day
  // floor for diagnostics.
  const durationDays = estimatedMinutes > 0 ? Math.max(1, Math.ceil(estimatedMinutes / 480)) : 0;
  const immobilizationDays = Math.max(restitutionMarginDays, durationDays, isDiagnostic ? 1 : 0, 1);
  // Forced: the margin is advisory, so the earliest pickup date is the drop-off day itself.
  const longMinDate = data.deposit_date
    ? (force ? data.deposit_date : addBusinessDays(data.deposit_date, immobilizationDays))
    : undefined;
  // Enforce a true N×24h immobilization: on the earliest allowed pickup date the vehicle
  // can't be collected before the time it was dropped off. A 15h-Monday drop with a 2-day
  // margin therefore becomes Wednesday 15h (48h), not Wednesday 08h (~41h). Forced, this
  // collapses to the physical rule — same-day pickup, but not before the drop-off hour.
  // Même forcé, la voiture reste au moins le temps des travaux (règle non forçable,
  // refusée aussi par le serveur — Jérôme, 2026-10-14).
  const workDoneAt = data.deposit_time
    ? (() => {
        const m = Math.min(slotToMinutes(data.deposit_time) + Math.max(0, estimatedMinutes), 23 * 60 + 59);
        return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
      })()
    : "";
  const pickupFloorOnMinDate =
    data.restitution_date && longMinDate && data.restitution_date === longMinDate
      ? (force && data.restitution_date === data.deposit_date ? workDoneAt : data.deposit_time)
      : "";
  /** A reception slot already taken. Forced, it no longer hides the slot — it only marks it. */
  const slotBusy = (s: string) => restUnavailableSet.has(s);
  const slotBlocked = (s: string) => !force && restUnavailableSet.has(s);
  const longPickupSlots = ALL_SLOTS.filter((s) => (!pickupFloorOnMinDate || s >= pickupFloorOnMinDate) && !slotBlocked(s));

  const getReadyTime = (time: string) => {
    if (estimatedReadyTimes[time]) return estimatedReadyTimes[time];
    return computeRestitutionTime(time, totalMinutes);
  };

  // The lunch-closure notice only applies when the car would *naturally* be ready
  // during the 12h–14h break, so pickup gets pushed to 14h. If it's ready at/after
  // 14h for its own reasons (e.g. deposited at 14h), the break doesn't concern it.
  const naturalReadyTime = data.deposit_time ? computeRestitutionTime(data.deposit_time, totalMinutes) : "";
  const readyDuringLunchClosure = naturalReadyTime >= "12:00" && naturalReadyTime < "14:00";

  // What waiting on site actually costs: measured from the real ready time, so the
  // lunch closure and the safety margin are already baked in.
  const waitReadyAt = data.deposit_time ? getReadyTime(data.deposit_time) : "";
  const waitMinutes = waitReadyAt && data.deposit_time
    ? Math.max(0, slotToMinutes(waitReadyAt) - slotToMinutes(data.deposit_time))
    : totalMinutes;

  // Auto-compute restitution for "wait on site"
  useEffect(() => {
    if (!isShort || data.wait_on_site !== true || !data.deposit_date || !data.deposit_time) return;
    const restTime = getReadyTime(data.deposit_time);
    update("restitution_date", data.deposit_date);
    update("restitution_time", restTime);
    update("mobility_option", "attente");
  }, [data.wait_on_site, data.deposit_date, data.deposit_time, isShort, totalMinutes, estimatedReadyTimes]);

  // Default the restitution for long interventions to the earliest allowed date; staff can
  // still pick a later date in the calendar below. Also snap an out-of-range earlier
  // selection up to the minimum (e.g. the deposit date moved past the old restitution).
  useEffect(() => {
    if (isShort || !data.deposit_date || !longMinDate) return;
    if (isDiagnostic && data.wait_on_site !== false) update("wait_on_site", false);
    if (!data.restitution_date || data.restitution_date < longMinDate) {
      update("restitution_date", longMinDate);
      if (!data.restitution_time) update("restitution_time", "18:00");
    }
    if (!data.mobility_option) update("mobility_option", "location");
  }, [data.deposit_date, isShort, isDiagnostic, longMinDate]);

  // On the earliest pickup date, snap any below-floor time up to the drop-off time (48h rule).
  useEffect(() => {
    if (isShort || !pickupFloorOnMinDate || !data.restitution_time) return;
    if (data.restitution_time < pickupFloorOnMinDate) update("restitution_time", pickupFloorOnMinDate);
  }, [isShort, pickupFloorOnMinDate, data.restitution_time]);

  const earliestPickup = useMemo(() => {
    if (!data.deposit_time) return "18:00";
    return getReadyTime(data.deposit_time);
  }, [data.deposit_time, totalMinutes, estimatedReadyTimes]);

  // Forced, the computed ready time stops being a floor (the shop decides the car is
  // done); only the drop-off hour still is. The suggestion below keeps using earliestPickup.
  const pickupFloor = force ? workDoneAt : earliestPickup;
  const pickupSlots = useMemo(
    () => ALL_SLOTS.filter((s) => s >= pickupFloor && !slotBlocked(s)),
    [pickupFloor, restUnavailableSet, force], // eslint-disable-line react-hooks/exhaustive-deps
  );
  // For "Non, je repasse" the client picks the pickup date (defaults to same day, but can be later).
  const pickupSameDay = !data.restitution_date || data.restitution_date === data.deposit_date;
  const pickupTimeSlots = pickupSameDay ? pickupSlots : ALL_SLOTS.filter((s) => !slotBlocked(s));

  useEffect(() => {
    if (!isShort || data.wait_on_site !== false || !data.deposit_date) return;
    // Default the pickup date to the drop-off day; never let it fall before the drop-off.
    if (!data.restitution_date || data.restitution_date < data.deposit_date) update("restitution_date", data.deposit_date);
    if (!data.mobility_option || data.mobility_option === "attente") update("mobility_option", "velo");
    // Same-day pickup can't be earlier than the ready time (forced: than the drop-off);
    // a later day allows any slot.
    if (pickupSameDay) {
      if (!data.restitution_time || data.restitution_time < pickupFloor) update("restitution_time", pickupSlots[0] || "18:00");
    } else if (!data.restitution_time) {
      update("restitution_time", pickupTimeSlots[0] || "");
    }
  }, [data.wait_on_site, data.mobility_option, data.deposit_date, data.restitution_date, isShort, pickupFloor, pickupSameDay]); // eslint-disable-line react-hooks/exhaustive-deps

  // A day with no free desk at all must not stay selected: slide to the next open
  // day (weekends and full days skipped) so the recap can never carry a date the
  // garage cannot honour. Forced, the staff's date stands: sliding it away would undo
  // the override they just asked for.
  useEffect(() => {
    if (force) return;
    if (section !== "restitution" || restFullDays === null || !data.restitution_date) return;
    const minD = (isShort ? data.deposit_date : longMinDate) || "";
    const full = new Set(restFullDays);
    const d = new Date((data.restitution_date < minD ? minD : data.restitution_date) + "T12:00:00");
    for (let i = 0; i < 60; i++) {
      const ds = toLocalDateStr(d);
      if (d.getDay() !== 0 && d.getDay() !== 6 && !full.has(ds)) {
        if (ds !== data.restitution_date) { update("restitution_date", ds); update("restitution_time", ""); }
        return;
      }
      d.setDate(d.getDate() + 1);
    }
  }, [restFullDays, data.restitution_date, section]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same for the hour: drop a slot the desk can't take and fall back to the closest
  // one that works (latest of the day for a long job, earliest for a same-day pickup).
  // Forced, a busy slot is a deliberate choice — only an empty one still gets a default.
  useEffect(() => {
    if (section !== "restitution" || restSlotsLoading || !data.restitution_date) return;
    if (data.restitution_time && (force || !restUnavailableSet.has(data.restitution_time))) return;
    const pool = isShort ? pickupTimeSlots : longPickupSlots;
    const next = (isShort ? pool[0] : pool[pool.length - 1]) || "";
    if (next !== data.restitution_time) update("restitution_time", next);
  }, [restUnavailable, restSlotsLoading, data.restitution_date, data.restitution_time, isShort]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedCard = NEED_CARDS.find((c) => c.id === data.intervention_type);
  const serviceLabel = data.dont_know_service
    ? "À déterminer avec le garage"
    : (selectedCard?.label || data.service || "Autre");
  // « Autre » s'ajoute aux prestations cochées (le back en fait une ligne à part) : ne
  // l'afficher que sans prestation faisait croire qu'il était ignoré.
  const interventionLabel = [...data.interventions, data.custom_intervention.trim()]
    .filter(Boolean).join(", ");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {/* One illustration, for the thing actually booked — the rows below stay
            typographic so the summary reads as data, not as decoration. */}
        {section === "recap" && (
          <Illus name={selectedCard?.id || "inconnu"} size={68} style={{ marginBottom: 6 }} />
        )}
        <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 26, lineHeight: 1.12, letterSpacing: "-0.032em", color: "#0A0A0A", margin: 0 }}>
          {section === "mobility" ? "Solution de mobilité" : section === "restitution" ? "Date de récupération" : "Récapitulatif"}
        </h2>
        {section === "mobility" && isShort && (
          <p style={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: "#A1A1A1", margin: 0 }}>
            Pendant l'immobilisation du véhicule.
          </p>
        )}
      </div>

      {/* Long / diagnostic: explain upfront why a mobility solution must be chosen */}
      {section === "mobility" && !isShort && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "12px 14px", borderRadius: 10, background: "#F7F7F7" }}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#6B7280", lineHeight: "19px" }}>
            Le véhicule doit rester au garage {immobilizationDays} jour{immobilizationDays > 1 ? "s" : ""}{isDiagnostic ? " pour réaliser le diagnostic" : ""}{force ? " (indicatif — RDV forcé, la restitution reste libre)" : ""}. Choisissez une solution de mobilité pour le client pendant cette période.
          </span>
        </div>
      )}

      {section === "recap" && (
        <>
      {/* Summary — one card, hairline-divided rows */}
      <div style={{ border: "1px solid #EDEDEF", borderRadius: 16, overflow: "hidden" }}>
        {/* Service */}
        <div style={{ display: "flex", alignItems: "flex-start", padding: "13px 16px", borderBottom: "1px solid #F3F3F4" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 500, color: "#A1A1A1", textTransform: "uppercase", letterSpacing: "0.04em" }}>Service</span>
            <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: 14, color: "#000" }}>{serviceLabel}</span>
            {interventionLabel && <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#A1A1A1" }}>{interventionLabel}</span>}
            {estimatedMinutes > 0 && <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#A1A1A1" }}>Durée estimée : {formatEstimatedDuration(estimatedMinutes)}</span>}
          </div>
        </div>

        {/* Vehicle */}
        <div style={{ display: "flex", alignItems: "flex-start", padding: "13px 16px", borderBottom: "1px solid #F3F3F4" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 500, color: "#A1A1A1", textTransform: "uppercase", letterSpacing: "0.04em" }}>Véhicule</span>
            <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 14, color: "#000", letterSpacing: 1 }}>{data.immatriculation}</span>
          </div>
        </div>

        {/* Date & time */}
        <div style={{ display: "flex", alignItems: "flex-start", padding: "13px 16px", borderBottom: "1px solid #F3F3F4" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 500, color: "#A1A1A1", textTransform: "uppercase", letterSpacing: "0.04em" }}>Date & heure</span>
            <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: 14, color: "#000", textTransform: "capitalize" }}>{formatDateFr(data.deposit_date)}</span>
            {data.deposit_time && <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#A1A1A1" }}>{formatSlot(data.deposit_time)}</span>}
          </div>
        </div>

        {/* Garage */}
        <div style={{ display: "flex", alignItems: "flex-start", padding: "13px 16px", borderBottom: "1px solid #F3F3F4" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 500, color: "#A1A1A1", textTransform: "uppercase", letterSpacing: "0.04em" }}>Garage</span>
            <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: 14, color: "#000" }}>{orgConfig.name}</span>
            {orgConfig.address && <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#A1A1A1" }}>{orgConfig.address}{orgConfig.postal || orgConfig.city ? `, ${orgConfig.postal} ${orgConfig.city}` : ""}</span>}
          </div>
        </div>

        {/* Mobilité */}
        <div style={{ display: "flex", alignItems: "flex-start", padding: "13px 16px", borderBottom: "1px solid #F3F3F4" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 500, color: "#A1A1A1", textTransform: "uppercase", letterSpacing: "0.04em" }}>Mobilité</span>
            <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: 14, color: "#000" }}>
              {data.wait_on_site === true ? "Attente sur place" : (mobilityOptions.find((o) => o.key === data.mobility_option)?.label || "—")}
            </span>
          </div>
        </div>

        {/* Récupération */}
        <div style={{ display: "flex", alignItems: "flex-start", padding: "13px 16px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 500, color: "#A1A1A1", textTransform: "uppercase", letterSpacing: "0.04em" }}>Récupération</span>
            <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: 14, color: "#000", textTransform: data.restitution_date ? "capitalize" : "none" }}>
              {data.restitution_date ? formatDateFr(data.restitution_date) : "Communiquée par le garage"}
            </span>
            {data.restitution_date && data.restitution_time && (
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#A1A1A1" }}>{formatSlot(data.restitution_time)}</span>
            )}
          </div>
        </div>
      </div>
        </>
      )}

      {section === "mobility" && (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {/* SHORT: wait on site question */}
        {isShort && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 13, color: "#666" }}>
              Souhaitez-vous attendre sur place ?
            </span>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {([true, false] as const).map((val) => {
                const selected = data.wait_on_site === val;
                return (
                  <button key={String(val)} onClick={() => { update("wait_on_site", val); if (val) update("mobility_option", "attente"); }}
                    style={{
                      display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 3,
                      fontFamily: "Inter, sans-serif", textAlign: "left",
                      padding: "13px 14px", borderRadius: 12, border: 0,
                      boxShadow: selected ? "inset 0 0 0 2px #0A0A0A" : "none",
                      background: selected ? "#fff" : "#F3F3F4",
                      cursor: "pointer",
                    }}>
                    {/* L'image dit le choix avant le texte. Fond blanc (pas de détourage) +
                        multiply : le blanc prend la couleur de la carte, grise ou blanche. */}
                    <Illus name={val ? "attente_sur_place" : "repasser_plus_tard"} size={96}
                      style={{ alignSelf: "center", mixBlendMode: "multiply", marginBottom: 6 }} />
                    <span style={{ fontWeight: 600, fontSize: 14, letterSpacing: "-0.01em", color: "#0A0A0A" }}>
                      {val ? "Oui, j'attends" : "Non, je repasse"}
                    </span>
                    {val ? (
                      <>
                        {waitMinutes > 0 && (
                          <span style={{ fontSize: 12, color: "#6B6B70", lineHeight: 1.35 }}>
                            ≈ {formatWaitDuration(waitMinutes)} d'attente
                          </span>
                        )}
                        {waitReadyAt && (
                          <span style={{ fontSize: 12, fontWeight: 600, color: "#0A0A0A", fontVariantNumeric: "tabular-nums" }}>
                            prêt vers {waitReadyAt.replace(":", "h")}
                          </span>
                        )}
                      </>
                    ) : (
                      <span style={{ fontSize: 12, color: "#6B6B70", lineHeight: 1.35 }}>Récupéré plus tard</span>
                    )}
                  </button>
                );
              })}
            </div>
            {data.wait_on_site === true && data.deposit_date && (
              <div className="bk-reveal" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {readyDuringLunchClosure && (
                  <div style={{ padding: "10px 14px", borderRadius: 10, background: "#F7F7F7" }}>
                    <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#6B7280" }}>
                      Nos locaux sont fermés au public de 12h à 14h (pause de l'équipe) : le véhicule est donc restitué à partir de 14h.
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Mobility options: shown when not waiting */}
        {((isShort && data.wait_on_site === false) || !isShort) && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div className="bkui-grid">
              {mobilityOptions.map((opt, i) => {
                const isSelected = data.mobility_option === opt.key;
                const unavailable = !!mobilityUnavailable[opt.key];
                return (
                  <button key={opt.key} disabled={unavailable}
                    className={`bkui-tile${isSelected ? " on" : ""}`}
                    style={{ animationDelay: `${i * 38}ms` }}
                    onClick={() => { if (!unavailable) update("mobility_option", opt.key); }}>
                    {isSelected && <span className="bkui-mark">{V2_MARK}</span>}
                    <Illus name={MOBILITY_ILLUS[opt.key] || "aucun"} />
                    <span className="bkui-tl">{opt.label}</span>
                    <span className="bkui-td">{unavailable ? "Indisponible" : opt.sub}</span>
                  </button>
                );
              })}
            </div>

            {/* Internal: choose the loaner vehicle (client gets auto-assigned).
                Collapsed by default — the current choice is shown; expand to override. */}
            {loanerUsage && data.deposit_date && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 12, letterSpacing: "0.07em", textTransform: "uppercase", color: "#A1A1A6" }}>Véhicule à attribuer</span>
                <button type="button" onClick={() => setLoanerOpen((o) => !o)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "13px 14px", borderRadius: 12, fontFamily: "Inter, sans-serif",
                    border: 0, background: "#F3F3F4", cursor: "pointer", textAlign: "left",
                  }}>
                  <span style={{ fontWeight: 500, fontSize: 14, color: "#0A0A0A", flex: 1 }}>{selectedLoanerLabel}</span>
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" style={{ transform: loanerOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
                    <path d="M6 9l6 6 6-6" stroke="#8A8A90" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {loanerOpen && (
                  <div className="bk-reveal" style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 260, overflowY: "auto" }}>
                    <button type="button" onClick={() => { update("rental_vehicle_id", ""); setLoanerOpen(false); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, fontFamily: "Inter, sans-serif",
                        border: 0, boxShadow: !data.rental_vehicle_id ? "inset 0 0 0 2px #0A0A0A" : "none",
                        background: !data.rental_vehicle_id ? "#fff" : "#FAFAFA", cursor: "pointer", textAlign: "left",
                      }}>
                      <span style={{ fontWeight: 500, fontSize: 14, color: "#0A0A0A" }}>Automatique (premier disponible)</span>
                    </button>
                    {loaners.map((v) => {
                      const sel = data.rental_vehicle_id === v.id;
                      const label = [v.name || [v.marque, v.modele].filter(Boolean).join(" "), v.plate].filter(Boolean).join(" · ") || "Véhicule";
                      return (
                        <button key={v.id} type="button" onClick={() => { update("rental_vehicle_id", v.id); setLoanerOpen(false); }}
                          style={{
                            display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, fontFamily: "Inter, sans-serif",
                            border: 0, boxShadow: sel ? "inset 0 0 0 2px #0A0A0A" : "none",
                            background: sel ? "#fff" : "#FAFAFA", cursor: "pointer", textAlign: "left",
                          }}>
                          <span style={{ fontWeight: 500, fontSize: 14, color: "#0A0A0A" }}>{label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {loaners.length === 0 && (
                  <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#6B7280" }}>Aucun véhicule libre sur le créneau — sera à attribuer manuellement.</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {section === "restitution" && (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Short + not waiting: pickup date + time */}
        {isShort && data.wait_on_site === false && data.mobility_option && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <MiniCalendar
              selectedDate={data.restitution_date}
              onSelect={(d) => { update("restitution_date", d); update("restitution_time", ""); }}
              minDate={data.deposit_date || undefined}
              fullDays={force ? [] : restFullDays}
              onMonthChange={fetchRestFullDays}
              rangeStart={data.deposit_date}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 13, color: "#666" }}>
                Heure de récupération{pickupSameDay && pickupFloor ? ` (à partir de ${pickupFloor.replace(":", "h")})` : ""}
              </span>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(74px, 1fr))", gap: 8 }}>
                {pickupTimeSlots.map((slot) => {
                  const isSelected = data.restitution_time === slot;
                  // Only ever true when forced — otherwise a busy slot isn't in the list.
                  const busy = slotBusy(slot);
                  return (
                    <button key={slot} onClick={() => update("restitution_time", slot)}
                      title={busy ? "Réception déjà occupée sur ce créneau" : undefined}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: 14,
                        padding: "11px 6px", borderRadius: 10,
                        border: isSelected ? "1.5px solid #000" : busy ? "1.5px solid #FECACA" : "1.5px solid #E5E5E5",
                        background: isSelected ? "#000" : busy ? "#FEF2F2" : "#fff",
                        color: isSelected ? "#fff" : busy ? "#991B1B" : "#000",
                        cursor: "pointer",
                      }}>
                      {formatSlot(slot)}
                    </button>
                  );
                })}
              </div>
              {pickupTimeSlots.length === 0 && !restSlotsLoading && (
                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#6B7280" }}>
                  Aucun créneau de récupération ce jour — choisissez une autre date.
                </span>
              )}
            </div>
          </div>
        )}

        {/* Long / diagnostic: always let the client choose a restitution date */}
        {!isShort && data.deposit_date && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {force ? (
              // The staff asked for the override: say what it bought them, and what the
              // normal rule would have imposed, so a same-day handover is a decision.
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 14px", borderRadius: 10, background: "#FEF2F2", border: "1px solid #FECACA" }}>
                <svg width={16} height={16} viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                  <path d="M8 1.6 15.2 14.4H0.8L8 1.6Z" stroke="#991B1B" strokeWidth={1.4} strokeLinejoin="round" />
                  <path d="M8 6.2v3.4" stroke="#991B1B" strokeWidth={1.4} strokeLinecap="round" />
                  <circle cx={8} cy={11.9} r={0.85} fill="#991B1B" />
                </svg>
                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#991B1B" }}>
                  RDV forcé — restitution libre, {immobilizationDays > 1 ? `la marge de ${immobilizationDays} jours` : "la marge d'immobilisation"} et les créneaux occupés en réception sont ignorés. Le jour même est possible{workDoneAt ? `, à partir de ${formatSlot(workDoneAt)} (fin des travaux)` : ""}.
                </span>
              </div>
            ) : (isDiagnostic || immobilizationDays >= 2) && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 14px", borderRadius: 10, background: "#F7F7F7" }}>
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#6B7280" }}>
                  Récupération possible à partir du {formatDateShort(longMinDate || null)}{data.deposit_time ? ` à ${formatSlot(data.deposit_time)}` : ""}.
                </span>
              </div>
            )}
            <MiniCalendar
              selectedDate={data.restitution_date}
              onSelect={(d) => { update("restitution_date", d); update("restitution_time", ""); }}
              minDate={longMinDate}
              fullDays={force ? [] : restFullDays}
              onMonthChange={fetchRestFullDays}
              rangeStart={data.deposit_date}
            />
            {data.restitution_date && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(74px, 1fr))", gap: 8 }}>
                {longPickupSlots.map((slot) => {
                  const isSelected = data.restitution_time === slot;
                  // Only ever true when forced — otherwise a busy slot isn't in the list.
                  const busy = slotBusy(slot);
                  return (
                    <button key={slot} onClick={() => update("restitution_time", slot)}
                      title={busy ? "Réception déjà occupée sur ce créneau" : undefined}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: 14,
                        padding: "11px 6px", borderRadius: 10,
                        border: isSelected ? "1.5px solid #000" : busy ? "1.5px solid #FECACA" : "1.5px solid #E5E5E5",
                        background: isSelected ? "#000" : busy ? "#FEF2F2" : "#fff",
                        color: isSelected ? "#fff" : busy ? "#991B1B" : "#000",
                        cursor: "pointer",
                      }}>
                      {formatSlot(slot)}
                    </button>
                  );
                })}
              </div>
            )}
            {data.restitution_date && longPickupSlots.length === 0 && !restSlotsLoading && (
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#6B7280" }}>
                Aucun créneau de récupération ce jour — choisissez une autre date.
              </span>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
}

// ─── Step 4 — Contact ───────────────────────────────────────

function StepContact({
  data, update, orgId,
}: {
  data: BookingData;
  update: (k: keyof BookingData, v: unknown) => void;
  orgId: string;
}) {
  const [clientLookup, setClientLookup] = useState<{ found: boolean; nom?: string; prenom?: string; email?: string; client_muted?: boolean; client_muted_at?: string | null; client_muted_by_name?: string | null; client_muted_reason?: string | null } | null>(null);
  // Sourdine d'un client choisi à l'étape véhicule (la recherche par numéro
  // ci-dessous ne tourne pas pour lui) : même lookup, seul l'état muet sert.
  const [selectedMute, setSelectedMute] = useState<typeof clientLookup>(null);
  const [lookingUp, setLookingUp] = useState(false);
  // When a client was already picked at the vehicle step, let staff still edit
  // the coordinates without re-searching.
  const [editingContact, setEditingContact] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A client identified from the plate can be missing key info (e.g. no phone
  // number on file). Open the editable form directly instead of the read-only
  // summary card, so staff can fill the gap without first clicking "Modifier".
  useEffect(() => {
    if (data.client_selected && !data.phone.trim()) setEditingContact(true);
  }, [data.client_selected, data.phone]);

  useEffect(() => {
    // A client picked earlier is already prefilled — don't re-search them here.
    if (data.client_selected) { setClientLookup(null); return; }
    const phone = data.phone.replace(/\s/g, "");
    if (phone.length < 10) { setClientLookup(null); return; }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLookingUp(true);
      try {
        const result = await bookingsApi.lookupClient(data.phone, orgId);
        setClientLookup(result);
        update("dossier_id", result.found ? result.dossier_id || "" : "");
        if (result.found) {
          if (result.nom) update("nom", result.nom);
          if (result.prenom) update("prenom", result.prenom);
          if (result.email) update("email", result.email);
          if (result.adresse) update("address", result.adresse);
          update("address_prefilled", !!result.adresse);
        } else {
          update("address_prefilled", false);
        }
      } catch { setClientLookup(null); }
      setLookingUp(false);
    }, 400);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [data.phone, orgId]);

  useEffect(() => {
    if (!data.client_selected) { setSelectedMute(null); return; }
    const phone = data.phone.replace(/\s/g, "");
    if (phone.length < 10) { setSelectedMute(null); return; }
    let cancelled = false;
    bookingsApi.lookupClient(data.phone, orgId)
      .then((r) => { if (!cancelled) setSelectedMute(r); })
      .catch(() => { if (!cancelled) setSelectedMute(null); });
    return () => { cancelled = true; };
  }, [data.client_selected, data.phone, orgId]);

  const recognized = clientLookup?.found === true;

  // Client déjà connu sous un autre numéro (TRANSPORTS GEANT, 2026-10-15) : le nom tapé
  // est cherché dans les dossiers et les plus proches sont proposés avant d'en créer un
  // nouveau.
  const [nameMatches, setNameMatches] = useState<ProjectHit[]>([]);
  const nameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const q = [data.nom, data.prenom].filter(Boolean).join(" ").trim();
    if (data.client_selected || recognized || data.dossier_id || q.length < 3) { setNameMatches([]); return; }
    if (nameTimerRef.current) clearTimeout(nameTimerRef.current);
    let cancelled = false;
    nameTimerRef.current = setTimeout(async () => {
      try {
        const hits = await searchDossiersFull(q, orgId);
        if (!cancelled) setNameMatches(hits.slice(0, 3));
      } catch { if (!cancelled) setNameMatches([]); }
    }, 400);
    return () => { cancelled = true; if (nameTimerRef.current) clearTimeout(nameTimerRef.current); };
  }, [data.nom, data.prenom, data.client_selected, data.dossier_id, recognized, orgId]);

  const pickKnownClient = async (hit: ProjectHit) => {
    setNameMatches([]);
    try {
      const dossier = await dossiersApi.get(hit.value);
      const contact = dossier.contacts?.find((c) => c.is_primary) || dossier.contacts?.[0];
      update("dossier_id", dossier.id);
      update("nom", contact?.nom || (contact ? "" : dossier.name) || data.nom);
      update("prenom", contact?.prenom || "");
      const phone = contact?.phone || dossier.phone;
      if (phone) update("phone", phone);
      const email = contact?.email || dossier.email;
      if (email) update("email", email);
      const adresse = contact?.adresse || dossier.address;
      if (adresse) update("address", adresse);
      update("address_prefilled", !!adresse);
      update("client_selected", true);
    } catch { /* staff can fill manually */ }
  };
  const mutedClient = clientLookup?.client_muted ? clientLookup : selectedMute?.client_muted ? selectedMute : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 26, lineHeight: 1.12, letterSpacing: "-0.032em", color: "#0A0A0A", margin: 0 }}>
          Vos coordonnées
        </h2>
        <p style={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: "#A1A1A1", margin: 0 }}>
          Ces informations sont utilisées uniquement pour votre réservation.
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {data.client_selected && !editingContact ? (
          // Client already chosen at the vehicle step — show a summary, no re-search.
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12, background: "#F7F7F7" }}>
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#000" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
            <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
              <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 14, color: "#000" }}>{[data.prenom, data.nom].filter(Boolean).join(" ") || "Client sélectionné"}</span>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#6B7280" }}>{[data.phone, data.email].filter(Boolean).join(" · ")}</span>
            </div>
            <button type="button" onClick={() => setEditingContact(true)}
              style={{ background: "none", border: "none", padding: 0, fontFamily: "Inter, sans-serif", fontSize: 12, color: "#000", textDecoration: "underline", cursor: "pointer", flexShrink: 0 }}>
              Modifier
            </button>
          </div>
        ) : (
          <>
            <TextField label="Téléphone" value={data.phone} onChange={(v) => update("phone", v.replace(/^\+33\s?/, "0"))} placeholder="06 12 34 56 78" type="phone" />
            {lookingUp && <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#A1A1A1", paddingLeft: 4 }}>Recherche...</span>}
            {/* Staff-only: say WHO the number belongs to, so the receptionist can
                check it's the right person before continuing. The client page
                deliberately never shows this — its anonymous lookup only answers
                found / has_address (see booking_public_router.lookup_client). */}
            {recognized && (
              <div className="bk-reveal" style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12, background: "#F7F7F7" }}>
                <svg width={18} height={18} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><path d="M5 13l4 4L19 7" stroke="#000" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
                <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                  <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#6B7280" }}>Client reconnu</span>
                  <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 14, color: "#000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {[clientLookup?.prenom, clientLookup?.nom].filter(Boolean).join(" ") || "Client existant (nom non renseigné)"}
                  </span>
                  {clientLookup?.email && (
                    <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{clientLookup.email}</span>
                  )}
                </div>
                {/* The RDV is attached to the dossier by phone number server-side, so
                    "not this client" can only mean "wrong number": clear it and the
                    prefilled identity, and let staff type the right one. */}
                <button type="button"
                  onClick={() => {
                    update("phone", "");
                    update("dossier_id", "");
                    update("nom", ""); update("prenom", ""); update("email", ""); update("address", "");
                    update("address_prefilled", false);
                    setClientLookup(null);
                  }}
                  style={{ background: "none", border: "none", padding: 0, fontFamily: "Inter, sans-serif", fontSize: 12, color: "#000", textDecoration: "underline", cursor: "pointer", flexShrink: 0 }}>
                  Ce n'est pas ce client
                </button>
              </div>
            )}

            {!recognized && (
              <>
                <TextField label="Email" value={data.email} onChange={(v) => update("email", v)} placeholder="votre@email.com" type="email" />
                <TextField label="Nom" value={data.nom} onChange={(v) => update("nom", v)} />
                <TextField label="Prénom" value={data.prenom} onChange={(v) => update("prenom", v)} />
                {nameMatches.length > 0 && (
                  <div className="bk-reveal" style={{ display: "flex", flexDirection: "column", gap: 6, padding: "10px 12px", borderRadius: 12, background: "#FFF7ED", border: "1px solid #FED7AA" }}>
                    <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 13, color: "#9A3412" }}>Client déjà connu ?</span>
                    {nameMatches.map((hit) => (
                      <button key={hit.value} type="button" onClick={() => pickKnownClient(hit)}
                        style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, padding: "8px 10px", borderRadius: 8, border: "1px solid #E5E5E5", background: "#FFF", cursor: "pointer", textAlign: "left", minWidth: 0 }}>
                        <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 14, color: "#000", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{hit.label}</span>
                        {(hit.extra_label || hit.extra_label_2) && (
                          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#6B7280", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {[hit.extra_label, hit.extra_label_2].filter(Boolean).join(" · ")}
                          </span>
                        )}
                      </button>
                    ))}
                    <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#9A3412" }}>Sinon, un nouveau client sera créé.</span>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Adresse — masquée uniquement quand un client connu avait DÉJÀ une
            adresse enregistrée (prefill). On se base sur address_prefilled, pas
            sur data.address, sinon le champ disparaît dès qu'on tape. */}
        {!((data.client_selected || recognized) && data.address_prefilled) && (
          <TextField label="Adresse" value={data.address} onChange={(v) => update("address", v)} placeholder="N° et rue, code postal, ville" />
        )}

        {/* Notification channel (internal) — WhatsApp or Email */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
          <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: 13, color: "#666" }}>Notifier le client par</span>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {([["whatsapp", "WhatsApp"], ["email", "Email"]] as const).map(([val, lbl]) => {
              const selected = (data.communication_channel || "whatsapp") === val;
              return (
                <button key={val} type="button" onClick={() => update("communication_channel", val)}
                  style={{
                    fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: 13, padding: "10px 12px", borderRadius: 12, cursor: "pointer",
                    border: selected ? "1.5px solid #000" : "1.5px solid #E5E5E5",
                    background: selected ? "#F5F5F5" : "#fff", color: selected ? "#000" : "#666",
                  }}>
                  {lbl}
                </button>
              );
            })}
          </div>
          {data.communication_channel === "email" && recognized && (
            <TextField label="Email" value={data.email} onChange={(v) => update("email", v)} placeholder="client@email.com" type="email" />
          )}
          {data.communication_channel === "email" && !data.email && !data.mute_client && (
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "#6B7280" }}>Renseignez un email pour notifier le client par email.</span>
          )}

          {/* Client DÉJÀ en sourdine (posée lors d'un RDV précédent) : sans ce
              bandeau, la confirmation ne part pas et personne ne le sait. */}
          {mutedClient && (
            <div className="bk-reveal" style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 6, padding: "10px 12px", borderRadius: 12, border: "1.5px solid #FCD34D", background: "#FFFBEB" }}>
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                <path d="M16.5 8.5a5 5 0 0 1 .5 2.2V13m-2 4H6a2 2 0 0 1-1.6-3.2C5.5 12.4 6 11 6 9.3a6 6 0 0 1 8-5.6" />
                <line x1="3" y1="3" x2="21" y2="21" />
              </svg>
              <span style={{ display: "block" }}>
                <span style={{ display: "block", fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 13, color: "#92400E" }}>
                  Ce client est en sourdine : il ne recevra pas la confirmation
                </span>
                <span style={{ display: "block", fontFamily: "Inter, sans-serif", fontSize: 11.5, color: "#92400E", marginTop: 2, lineHeight: 1.4 }}>
                  Ni rappels ni messages non plus
                  {mutedClient.client_muted_by_name ? ` — coupé par ${mutedClient.client_muted_by_name}` : ""}
                  {formatMuteDate(mutedClient.client_muted_at) ? ` le ${formatMuteDate(mutedClient.client_muted_at)}` : ""}
                  {muteReasonLabel(mutedClient.client_muted_reason) ? ` (${muteReasonLabel(mutedClient.client_muted_reason)})` : ""}.
                  {" "}Pour le prévenir, réactivez ses notifications depuis sa fiche client ou le chat.
                </span>
              </span>
            </div>
          )}
          {/* Sourdine complète — pour les RDV qu'on ne veut surtout pas voir partir
              chez le client (véhicule interne, atelier, client qui a demandé le
              silence). Coupe TOUT (confirmation, rappels, relances devis, suivi,
              messages manuels) ; seul un « Réactiver » depuis le chat de l'ordre
              ou du dossier la lève. */}
          <label
            style={{
              display: "flex", alignItems: "flex-start", gap: 10, marginTop: 6, cursor: "pointer",
              padding: "10px 12px", borderRadius: 12,
              border: data.mute_client ? "1.5px solid #B45309" : "1.5px solid #E5E5E5",
              background: data.mute_client ? "#FFFBEB" : "#fff",
            }}
          >
            <input
              type="checkbox"
              checked={data.mute_client}
              onChange={(e) => update("mute_client", e.target.checked)}
              style={{ width: 16, height: 16, marginTop: 1, accentColor: "#B45309", cursor: "pointer" }}
            />
            <span style={{ display: "block" }}>
              <span style={{ display: "block", fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 13, color: data.mute_client ? "#92400E" : "#0A0A0A" }}>
                Ne pas notifier le client
              </span>
              <span style={{ display: "block", fontFamily: "Inter, sans-serif", fontSize: 11.5, color: "#6B7280", marginTop: 2, lineHeight: 1.4 }}>
                Met le client en sourdine : aucun email, WhatsApp ou SMS ne partira vers lui
                (confirmation, rappels, relances, suivi), tant qu'il n'est pas réactivé
                depuis le chat ou la fiche, ou qu'il ne reprend pas RDV lui-même en ligne.
              </span>
            </span>
          </label>
        </div>

      </div>
    </div>
  );
}

// ─── Step 5 — Carte grise ───────────────────────────────────

/** How the carte grise gets filed for this RDV: staff drop it here and now, or the
 *  client photographs it from their phone via the tracking link. */
export type CarteGriseChoice = "staff" | "client" | null;

/** Same cap as the public validation endpoint (20 Mo). */
const MAX_CARTE_GRISE_SIZE = 20 * 1024 * 1024;

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} Mo`;
}

function StepCarteGrise({ choice, setChoice, file, setFile, plate, check, setCheck }: {
  choice: CarteGriseChoice;
  setChoice: (c: CarteGriseChoice) => void;
  file: File | null;
  setFile: (f: File | null) => void;
  /** Plate typed on the vehicle step — what the AI checks the carte grise against. */
  plate: string;
  /** Result of that check, kept while staff move between steps. */
  check: CarteGriseValidationResult | null;
  setCheck: (r: CarteGriseValidationResult | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  // File waiting for the AI check — the same overlay the client sees on the tracking
  // page. Staff get the analysis too; only the filing differs (after the RDV exists).
  const [analyzing, setAnalyzing] = useState<File | null>(null);

  // Image preview — revoked on change so a long booking session doesn't leak blobs.
  useEffect(() => {
    if (!file || !file.type.startsWith("image/")) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const acceptFile = (f: File | null | undefined) => {
    if (!f) return;
    const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name);
    if (!isPdf && !f.type.startsWith("image/")) {
      setFileError("Format non accepté — déposez un PDF ou une image.");
      return;
    }
    if (f.size > MAX_CARTE_GRISE_SIZE) {
      setFileError("Fichier trop volumineux (20 Mo maximum).");
      return;
    }
    setFileError(null);
    setCheck(null);
    setAnalyzing(f);
  };

  const options: { key: "staff" | "client"; label: string; sub: string }[] = [
    { key: "staff", label: "Je la dépose maintenant", sub: "PDF ou photo — scan, copie ou original du client" },
    { key: "client", label: "Le client la déposera", sub: "Il la photographie depuis son téléphone, via le lien de suivi" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Illus name="carte_grise" size={68} style={{ flexShrink: 0 }} />
        <div>
          <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 26, lineHeight: 1.12, letterSpacing: "-0.032em", color: "#0A0A0A", margin: 0 }}>
            Carte grise
          </h2>
          <p style={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: "#A1A1A1", margin: 0, marginTop: 4 }}>
            Elle est nécessaire pour préparer l'ordre de réparation.
          </p>
        </div>
      </div>

      {/* The plate the carte grise must match — staff often juggle several
          vehicles for one client, so it is restated right above the choice. */}
      {plate && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "6px 12px 6px 8px", borderRadius: 8,
            border: "1.5px solid #D4D4D4", background: "#fff",
            fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 16, letterSpacing: "0.08em", color: "#0A0A0A",
          }}>
            <span style={{ width: 14, alignSelf: "stretch", borderRadius: 4, background: "#1D4ED8", flexShrink: 0 }} />
            {formatPlateV2(plate) || plate}
          </span>
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#8A8A8A" }}>Véhicule du rendez-vous</span>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {options.map((o) => {
          const selected = choice === o.key;
          return (
            <button key={o.key} type="button"
              onClick={() => { setChoice(o.key); if (o.key === "client") { setFile(null); setCheck(null); setFileError(null); } }}
              style={{
                display: "flex", alignItems: "center", gap: 12, textAlign: "left",
                padding: "14px 16px", borderRadius: 14, cursor: "pointer",
                border: selected ? "1.5px solid #000" : "1.5px solid #E5E5E5",
                background: selected ? "#FAFAFA" : "#fff",
              }}>
              <CarteGriseOptionIcon variant={o.key} active={selected} />
              <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
                <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 14, color: "#0A0A0A" }}>{o.label}</span>
                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#8A8A8A", lineHeight: 1.35 }}>{o.sub}</span>
              </div>
              <span style={{
                width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                border: selected ? "5px solid #000" : "1.5px solid #D4D4D4",
                background: "#fff",
              }} />
            </button>
          );
        })}
      </div>

      {/* Staff upload — drop zone, then the picked file with a preview */}
      {choice === "staff" && (
        <div className="bk-reveal" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <input
            ref={inputRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }}
            onChange={(e) => { acceptFile(e.target.files?.[0]); e.target.value = ""; }}
          />
          {!file ? (
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); acceptFile(e.dataTransfer.files?.[0]); }}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
                padding: "26px 16px", borderRadius: 14, cursor: "pointer",
                border: `1.5px dashed ${dragging ? "#0A0A0A" : "#D4D4D4"}`,
                background: dragging ? "#F5F5F5" : "#FCFCFC",
              }}>
              <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 16V4M12 4L7.5 8.5M12 4l4.5 4.5" /><path d="M4 16v2.5A1.5 1.5 0 005.5 20h13a1.5 1.5 0 001.5-1.5V16" />
              </svg>
              <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 14, color: "#0A0A0A" }}>Choisir un fichier</span>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#A1A1A1" }}>PDF ou image · 20 Mo maximum</span>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 10, borderRadius: 14, border: "1.5px solid #E5E5E5", background: "#fff" }}>
              {previewUrl ? (
                <img src={previewUrl} alt="" style={{ width: 56, height: 44, objectFit: "cover", borderRadius: 8, flexShrink: 0, border: "1px solid #EDEDEF" }} />
              ) : (
                <div style={{ width: 56, height: 44, borderRadius: 8, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6.5A1.5 1.5 0 0 0 5 3.5v17A1.5 1.5 0 0 0 6.5 22h11a1.5 1.5 0 0 0 1.5-1.5V7z" /><path d="M14 2v5h5" />
                  </svg>
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
                <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 13, color: "#0A0A0A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#A1A1A1" }}>{formatFileSize(file.size)}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
                <button type="button" onClick={() => inputRef.current?.click()}
                  style={{ background: "none", border: "none", padding: 0, fontFamily: "Inter, sans-serif", fontSize: 12, color: "#0A0A0A", textDecoration: "underline", cursor: "pointer" }}>
                  Remplacer
                </button>
                <button type="button" onClick={() => { setFile(null); setCheck(null); setFileError(null); }}
                  style={{ background: "none", border: "none", padding: 0, fontFamily: "Inter, sans-serif", fontSize: 12, color: "#9CA3AF", textDecoration: "underline", cursor: "pointer" }}>
                  Retirer
                </button>
              </div>
            </div>
          )}
          {fileError && (
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#B91C1C" }}>{fileError}</span>
          )}
          {file && check && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "9px 12px", borderRadius: 10, background: check.valid ? "#F0FDF4" : "#FEF2F2" }}>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: check.valid ? "#166534" : "#991B1B", lineHeight: 1.4 }}>
                {check.valid
                  ? `Analyse IA : carte grise reconnue${check.immatriculation_lue ? ` (${check.immatriculation_lue})` : ""}.`
                  : `Analyse IA : ${check.message} — fichier conservé.`}
              </span>
            </div>
          )}
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "#A1A1A1" }}>
            Le fichier est classé dans le dossier du client juste après la confirmation du RDV.
          </span>
        </div>
      )}

      {/* Same analysis screens as the client tracking page — nothing is uploaded here:
          the file is filed on the ordre right after the RDV is confirmed. */}
      {analyzing && (
        <CarteGriseUploadOverlay
          file={analyzing}
          staff
          plate={plate}
          onClose={() => { setAnalyzing(null); inputRef.current?.click(); }}
          onSuccess={(result) => { setFile(analyzing); setCheck(result || null); setAnalyzing(null); }}
        />
      )}

      {choice === "client" && (
        <div className="bk-reveal" style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", borderRadius: 12, background: "#F7F7F7" }}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
            <rect x="6" y="2" width="12" height="20" rx="2.5" /><path d="M11 18.5h2" />
          </svg>
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "#374151", lineHeight: 1.45 }}>
            Le client reçoit son lien de suivi avec la confirmation, et il est relancé tant que la carte
            grise manque. Pensez à la lui demander de vive voix.
          </span>
        </div>
      )}
    </div>
  );
}

function CarteGriseOptionIcon({ variant, active }: { variant: "staff" | "client"; active: boolean }) {
  const stroke = active ? "#0A0A0A" : "#8A8A8A";
  return (
    <div style={{ width: 38, height: 38, borderRadius: 10, background: active ? "#EFEFEF" : "#F7F7F7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {variant === "staff" ? (
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 16V4M12 4L7.5 8.5M12 4l4.5 4.5" /><path d="M4 16v2.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V16" />
        </svg>
      ) : (
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
          <rect x="6" y="2" width="12" height="20" rx="2.5" /><path d="M11 18.5h2" />
        </svg>
      )}
    </div>
  );
}

// ─── Confirmation ───────────────────────────────────────────

function buildCalendarUrl(data: BookingData, orgName: string): string {
  if (!data.deposit_date || !data.deposit_time) return "";
  const [h, m] = data.deposit_time.split(":").map(Number);
  const start = new Date(`${data.deposit_date}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`);
  const end = new Date(start.getTime() + 60 * 60 * 1000); // 1h block
  const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d+/, "");
  const title = encodeURIComponent(`RDV ${orgName} - ${data.immatriculation}`);
  const details = encodeURIComponent(`Rendez-vous garage ${orgName}\nVéhicule : ${data.immatriculation}`);
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(start)}/${fmt(end)}&details=${details}`;
}

/** Chiffres d'un numéro français ramené à 0XXXXXXXXX ("" si ce n'en est pas un). */
function frenchDigits(phone: string): string {
  let d = (phone || "").replace(/\D/g, "");
  if (d.startsWith("0033")) d = "0" + d.slice(4);
  else if (d.startsWith("33") && d.length === 11) d = "0" + d.slice(2);
  return d.length === 10 && d.startsWith("0") ? d : "";
}

/** Fixe français (01-05, 08, 09) : jamais sur WhatsApp. Jumeau de la vue client
 *  (BookingFullPage) et de phone_line_kind côté serveur. */
function isFrenchLandline(phone: string): boolean {
  const d = frenchDigits(phone);
  return !!d && !"67".includes(d[1]);
}

function ConfirmationScreen({ data, bookingId, trackingUrl, whatsappPhone, orgConfig, internalMode, onClose, carteGriseStatus, onRetryCarteGrise, carteGriseRetrying, whatsappConfirmationSent }: {
  data: BookingData; bookingId: string; trackingUrl: string; whatsappPhone: string; orgConfig: OrgConfig; internalMode?: boolean; onClose?: () => void;
  /** What the server said: the WhatsApp confirmation actually left with the booking. */
  whatsappConfirmationSent?: boolean;
  /** Internal only — what became of the carte grise for this RDV. */
  carteGriseStatus?: "sent" | "client" | "failed" | "none";
  onRetryCarteGrise?: () => void;
  carteGriseRetrying?: boolean;
}) {
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const ref = bookingId ? `#${bookingId.slice(0, 8).toUpperCase()}` : "";
  const depositLabel = data.deposit_date ? `${formatDateShort(data.deposit_date)} à ${data.deposit_time}` : "";
  const waMessage = `Bonjour, je confirme mon RDV ${ref} du ${depositLabel} pour mon véhicule ${data.immatriculation || ""}. Merci !`;
  const waLink = whatsappPhone
    ? `https://wa.me/${whatsappPhone.replace(/[^0-9+]/g, "")}?text=${encodeURIComponent(waMessage)}`
    : null;
  const hasEmail = !!data.email;
  const selectedCard = NEED_CARDS.find((c) => c.id === data.intervention_type);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 24, flex: 1, paddingTop: 20 }}>
      {/* Big checkmark — pops in, then the stroke draws itself */}
      <div className="bkui-hero">
        <Illus name="confirme" size={148} />
      </div>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
        <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 22, color: "#000" }}>
          {internalMode ? "RDV créé !" : "Réservation confirmée !"}
        </span>
        <span style={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: "#A1A1A1", textAlign: "center" }}>
          {internalMode ? "Le client a été notifié." : "Nous avons hâte de vous accueillir."}
        </span>
      </div>

      {/* Details */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #F3F4F6" }}>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="3" /><path d="M3 10h18M8 2v4M16 2v4" />
          </svg>
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: "#000", textTransform: "capitalize" }}>
            {formatDateFr(data.deposit_date)} à {data.deposit_time ? formatSlot(data.deposit_time) : ""}
          </span>
        </div>
        {selectedCard && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #F3F4F6" }}>
            <ServiceIcon type={selectedCard.iconType} size={18} color="#666" />
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: "#000" }}>{selectedCard.label}</span>
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #F3F4F6" }}>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="3" width="22" height="18" rx="3" /><line x1="1" y1="9" x2="23" y2="9" />
          </svg>
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: "#000" }}>{data.immatriculation}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #F3F4F6" }}>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
          </svg>
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: "#000" }}>{orgConfig.name}</span>
        </div>
      </div>

      {/* Internal: notification status + close */}
      {internalMode && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
          {data.communication_channel !== "email" && data.phone && whatsappConfirmationSent && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#16A34A" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#166534" }}>Confirmation WhatsApp envoyée au client ({data.phone})</span>
            </div>
          )}
          {data.communication_channel !== "email" && data.phone && !whatsappConfirmationSent && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: "#F7F7F7" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#374151" }}>
                {data.mute_client
                  ? "Client en sourdine : aucune confirmation envoyée"
                  : isFrenchLandline(data.phone)
                    ? `Numéro fixe (${data.phone}) : pas de confirmation WhatsApp`
                    : `Confirmation WhatsApp non envoyée (${data.phone}) — une relance partira dans 10 min`}
              </span>
            </div>
          )}
          {data.communication_channel === "email" && data.email && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: "#F7F7F7" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 7L2 7" /></svg>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#374151" }}>Email de confirmation envoyé ({data.email})</span>
            </div>
          )}
          {carteGriseStatus === "sent" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#16A34A" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#166534" }}>Carte grise déposée</span>
            </div>
          )}
          {carteGriseStatus === "client" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: "#F7F7F7" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="2" width="12" height="20" rx="2.5" /><path d="M11 18.5h2" /></svg>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#374151" }}>Carte grise à déposer par le client</span>
            </div>
          )}
          {carteGriseStatus === "failed" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: "#FEF2F2", border: "1px solid #FECACA" }}>
              <svg width={14} height={14} viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                <path d="M8 1.6 15.2 14.4H0.8L8 1.6Z" stroke="#991B1B" strokeWidth={1.4} strokeLinejoin="round" />
                <path d="M8 6.2v3.4" stroke="#991B1B" strokeWidth={1.4} strokeLinecap="round" />
                <circle cx={8} cy={11.9} r={0.85} fill="#991B1B" />
              </svg>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#991B1B", flex: 1 }}>Carte grise non envoyée</span>
              <button type="button" onClick={onRetryCarteGrise} disabled={carteGriseRetrying}
                style={{ background: "none", border: "none", padding: 0, fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 12, color: "#991B1B", textDecoration: "underline", cursor: carteGriseRetrying ? "default" : "pointer", flexShrink: 0 }}>
                {carteGriseRetrying ? "Envoi…" : "Réessayer"}
              </button>
            </div>
          )}
          <button onClick={onClose}
            style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 15, padding: "14px 20px", borderRadius: 12, background: "#000", color: "#fff", border: "none", cursor: "pointer", width: "100%", marginTop: 4 }}>
            Fermer
          </button>
        </div>
      )}

      {/* WhatsApp / Email confirm (client-facing) */}
      {!internalMode && (!emailConfirmed ? (
        <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: 8 }}>
          {waLink && (
            <a href={waLink} target="_blank" rel="noopener noreferrer"
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                width: "100%", padding: "14px 20px", background: "#25D366", color: "#fff",
                fontFamily: "Inter, sans-serif", borderRadius: 12, textDecoration: "none", cursor: "pointer",
              }}>
              <svg width="22" height="22" viewBox="0 0 32 32" fill="white" style={{ flexShrink: 0 }}>
                <path fillRule="evenodd" clipRule="evenodd" d="M16 3C8.82 3 3 8.82 3 16c0 2.49.7 4.81 1.91 6.78L3 29l6.42-1.83A12.94 12.94 0 0016 29c7.18 0 13-5.82 13-13S23.18 3 16 3zm0 23.5c-2.17 0-4.2-.62-5.93-1.7l-.42-.26-3.82 1.09 1.02-3.72-.28-.44A10.44 10.44 0 015.5 16C5.5 10.2 10.2 5.5 16 5.5S26.5 10.2 26.5 16 21.8 26.5 16 26.5zm5.95-7.8c-.33-.16-1.93-.95-2.23-1.06-.3-.11-.52-.16-.73.16-.22.33-.84 1.06-1.03 1.28-.19.22-.38.24-.71.08-.33-.16-1.38-.51-2.63-1.62-.97-.87-1.63-1.93-1.82-2.26-.19-.33-.02-.5.14-.67.15-.14.33-.38.49-.57.16-.19.22-.33.33-.55.11-.22.05-.41-.03-.57-.08-.16-.73-1.77-1-2.42-.27-.64-.54-.55-.73-.56h-.63c-.22 0-.57.08-.87.41-.3.33-1.14 1.11-1.14 2.72 0 1.6 1.17 3.15 1.33 3.37.16.22 2.3 3.51 5.57 4.92.78.34 1.39.54 1.86.69.78.25 1.49.21 2.05.13.63-.09 1.93-.79 2.2-1.55.28-.76.28-1.41.19-1.55-.08-.14-.3-.22-.63-.38z" />
              </svg>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>Confirmer via WhatsApp</span>
                <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.85 }}>Discutez en direct avec le garage</span>
              </div>
            </a>
          )}
          {hasEmail && (
            <button onClick={() => { setEmailConfirmed(true); }}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                width: "100%", padding: "14px 20px", background: "#fff", color: "#374151",
                fontFamily: "Inter, sans-serif", fontSize: 14, fontWeight: 600, borderRadius: 12,
                border: "1.5px solid #E5E5E5", cursor: "pointer",
              }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 7L2 7" />
              </svg>
              Confirmer par email
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", borderRadius: 10, background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#16A34A" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#16A34A" }}>Email de confirmation envoyé à {data.email}</span>
          </div>
          {trackingUrl && (
            <a href={trackingUrl} target="_blank" rel="noopener noreferrer"
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: "100%", padding: "14px 20px", background: "#000", color: "#fff",
                fontFamily: "Inter, sans-serif", fontSize: 14, fontWeight: 600, borderRadius: 12,
                textDecoration: "none", cursor: "pointer",
              }}>
              Suivre ma demande
            </a>
          )}
        </div>
      ))}

      {/* SMS reminder note (client-facing) */}
      {!internalMode && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: "#F9FAFB" }}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#6B7280" }}>
            Vous recevrez un SMS de rappel avant votre rendez-vous.
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Main Full Page Component ───────────────────────────────

/** Client et véhicule d'un ordre existant, pour un RDV secondaire. */
export type BookingPrefill = { plate: string; marque?: string; modele?: string; dossier_id?: string; client_name?: string };

interface Props {
  onClose: () => void;
  /** Reçoit l'id de l'ordre créé par le RDV. */
  onSuccess?: (ordreId?: string) => void;
  /** Ouvre la prise de RDV avec ce véhicule et son client déjà choisis. */
  prefill?: BookingPrefill;
  /** Internal staff mode (relaxed margins, client/immat search). Always true here. */
  internalMode?: boolean;
}

/** Fausse barre de défilement du cadre de réservation.
 *
 *  Le conteneur défilant masque sa barre native (.bkui-scroll) : sans elle, rien ne
 *  signalait qu'il restait des cartes sous le pied de page. Cette barre fine est
 *  dessinée par-dessus, à droite, entre le haut du conteneur et le pied (qui flotte
 *  sur le contenu, d'où `bottomInset`). Elle n'apparaît que s'il y a du contenu caché,
 *  suit le défilement et se glisse à la souris comme au doigt.
 */
function BookingScrollHint({ scrollerRef, bottomInset, watchKey }: {
  scrollerRef: React.RefObject<HTMLDivElement | null>;
  bottomInset: number;
  watchKey: string;
}) {
  const [m, setM] = useState<{ top: number; track: number; thumb: number; pos: number } | null>(null);
  const dragRef = useRef<{ startY: number; startScroll: number; ratio: number } | null>(null);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const measure = () => {
      const max = el.scrollHeight - el.clientHeight;
      if (max <= 4) { setM(null); return; }
      const top = el.offsetTop + 8;
      const track = el.clientHeight - bottomInset - 16;
      if (track < 40) { setM(null); return; }
      // Le pied couvre `bottomInset` px du bas : la partie réellement lisible est
      // clientHeight - bottomInset, sur un contenu de scrollHeight - bottomInset.
      const visible = Math.max(el.clientHeight - bottomInset, 1);
      const content = Math.max(el.scrollHeight - bottomInset, visible);
      const thumb = Math.max(28, Math.round((track * visible) / content));
      const pos = Math.round(((track - thumb) * el.scrollTop) / max);
      setM({ top, track, thumb, pos });
    };
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    // Le contenu change de hauteur (étape suivante, images qui chargent, sous-liste).
    Array.from(el.children).forEach((c) => ro.observe(c));
    return () => { el.removeEventListener("scroll", measure); ro.disconnect(); };
  }, [scrollerRef, bottomInset, watchKey]);

  if (!m) return null;

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    if (!el) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const max = el.scrollHeight - el.clientHeight;
    dragRef.current = { startY: e.clientY, startScroll: el.scrollTop, ratio: max / Math.max(m.track - m.thumb, 1) };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    const el = scrollerRef.current;
    if (!d || !el) return;
    el.scrollTop = d.startScroll + (e.clientY - d.startY) * d.ratio;
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };

  // Juste un rectangle gris clair, sans rail : discret, mais il dit « ça continue ».
  return (
    <div aria-hidden style={{
      position: "absolute", right: 6, top: m.top, height: m.track, width: 4, zIndex: 15,
    }}>
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{
          // Zone de saisie plus large que le trait visible (5 px, c'est fin au doigt).
          position: "absolute", left: -6, right: -6, top: m.pos, height: m.thumb,
          cursor: "grab", touchAction: "none",
        }}
      >
        <div style={{ position: "absolute", left: 6, right: 6, top: 0, bottom: 0, borderRadius: 999, background: "rgba(0,0,0,0.16)" }} />
      </div>
    </div>
  );
}

export default function InternalBookingPage({ onClose, onSuccess, internalMode = true, prefill }: Props) {
  const orgData = useDataStore((s) => s.orgData);
  const orgId = orgData?.id ?? "";
  const orgConfig: OrgConfig = {
    name: orgData?.infos?.name ?? "",
    address: orgData?.infos?.address,
    postal: orgData?.infos?.postal,
    city: orgData?.infos?.city,
    booking_min_days_ahead: orgData?.config?.booking_min_days_ahead,
    booking_max_days_ahead: orgData?.config?.booking_max_days_ahead,
    booking_sr_min_hours_ahead: orgData?.config?.booking_sr_min_hours_ahead,
    booking_safety_margin_hours: orgData?.config?.booking_safety_margin_hours,
    booking_mobility_options: orgData?.config?.booking_mobility_options,
  };
  const promotions = useMemo(() => activePromotions(orgData?.config?.booking_promotions), [orgData?.config?.booking_promotions]);
  // Internal staff bypass the public booking delays.
  const minDaysAhead = internalMode ? 0 : (orgConfig.booking_min_days_ahead ?? 1);
  const maxDaysAhead = internalMode ? 365 : (orgConfig.booking_max_days_ahead ?? 90);
  const srMinHoursAhead = internalMode ? 0 : (orgConfig.booking_sr_min_hours_ahead ?? 2);
  const safetyMarginHours = orgConfig.booking_safety_margin_hours ?? 1;

  const [step, setStep] = useState(0);
  const [data, setData] = useState<BookingData>({ ...INITIAL });
  const [confirmed, setConfirmed] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [bookingId, setBookingId] = useState<string>("");
  const [estimatedReadyTimes, setEstimatedReadyTimes] = useState<Record<string, string>>({});
  const [trackingUrl, setTrackingUrl] = useState<string>("");
  const [whatsappPhone, setWhatsappPhone] = useState<string>("");
  const [waConfirmationSent, setWaConfirmationSent] = useState(false);
  // Recall campaign — checked against ServiceBox as soon as the plate is known.
  const [campaignCheck, setCampaignCheck] = useState<CampaignCheck | null>(null);
  const [campaignRequestSent, setCampaignRequestSent] = useState(false);
  // Carte grise — last step. Either staff drop the file here (uploaded right after
  // the ordre exists) or they acknowledge the client will send it from their phone.
  const [carteGriseChoice, setCarteGriseChoice] = useState<CarteGriseChoice>(null);
  const [carteGriseFile, setCarteGriseFile] = useState<File | null>(null);
  const [carteGriseCheck, setCarteGriseCheck] = useState<CarteGriseValidationResult | null>(null);
  const [carteGriseFailed, setCarteGriseFailed] = useState(false);
  const [carteGriseRetrying, setCarteGriseRetrying] = useState(false);
  const [ordreId, setOrdreId] = useState<string>("");
  // Staff force / atelier-urgency override (sends force:true + urgent availability).
  const [forceSlot, setForceSlot] = useState(false);
  const [urgentMode, setUrgentMode] = useState(false);
  // Internal client/immat selection — lifted so it survives step navigation.
  const [clientQuery, setClientQuery] = useState("");
  const [clientVehicles, setClientVehicles] = useState<ClientVehicle[]>([]);
  const [payerOptions, setPayerOptions] = useState<PayerOption[]>([]);
  const [payerKey, setPayerKey] = useState<string | null>(null);
  const [addingVehicle, setAddingVehicle] = useState(false);
  const [pendingPrefill, setPendingPrefill] = useState<BookingPrefill | null>(prefill ?? null);
  // Scroll the content back to the top on every step change so a long step
  // followed by a short one doesn't leave the panel scrolled mid-way (jank).
  const contentRef = useRef<HTMLDivElement>(null);
  // The footer floats over the scroll area and its height varies (blocking reasons,
  // "Urgence" switch…). Measure it so the content's bottom padding always clears it —
  // a hardcoded padding left the last field half-hidden and unreachable (no scroll).
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerH, setFooterH] = useState(124);

  useEffect(() => {
    if (!orgId) return;
    interventionsApi.list(orgId).then(setInterventions).catch(() => {});
  }, [orgId]);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [step, confirmed]);

  const update = (key: keyof BookingData, value: unknown) => setData((prev) => ({ ...prev, [key]: value }));

  // Canal par défaut d'après le numéro, dès qu'il est complet (saisi ou prérempli) :
  // un mobile → WhatsApp, la confirmation part dessus avec le RDV ; un fixe avec un
  // email connu → email (WhatsApp ne l'atteindra jamais). Réappliqué seulement quand le NUMÉRO change,
  // pour ne jamais écraser un choix fait à la main ensuite. Même règle côté client.
  const autoChannelPhoneRef = useRef("");
  useEffect(() => {
    const digits = frenchDigits(data.phone);
    if (!digits || digits === autoChannelPhoneRef.current) return;
    autoChannelPhoneRef.current = digits;
    setData((prev) => {
      const want = !isFrenchLandline(prev.phone) ? "whatsapp" : prev.email ? "email" : prev.communication_channel;
      return prev.communication_channel === want ? prev : { ...prev, communication_channel: want };
    });
  }, [data.phone]);

  // Multiple interventions (possibly across categories) can be selected.
  const selectedIntvs = useMemo(() => interventions.filter((i) => data.interventions.includes(i.name)), [interventions, data.interventions]);
  // Prestations « précision requise » encore sans précision : elles bloquent l'étape
  // et le bouton « Ajouter » du sous-menu.
  const missingDetails = useMemo(
    () => selectedIntvs.filter((i) => i.requires_details && (data.intervention_details[i.name] || "").trim().length < MIN_DETAILS_CHARS),
    [selectedIntvs, data.intervention_details],
  );
  // "Fast" / express only apply when EVERY selected intervention qualifies (otherwise the slow workshop drives the flow).
  const { isCampagne, isFast, isExpress } = zoneFlags(selectedIntvs, data.custom_intervention);
  const campagneSelected = selectedIntvs.some((i) => i.type === CAMPAGNE_INTERVENTION_TYPE);
  const campaignPlate = useMemo(
    () => data.immatriculation.replace(/[^A-Za-z0-9]/g, "").toUpperCase(),
    [data.immatriculation],
  );

  // A campaign is the one motive we can verify. Checked here rather than inside
  // StepVehicle so the result survives step navigation and drives the step's
  // blocking reasons. ServiceBox is queried with the plate as typed, exactly like
  // the vehicle lookup; the 500 ms debounce absorbs the keystrokes.
  useEffect(() => {
    if (!campagneSelected || !orgId || campaignPlate.length < 7) { setCampaignCheck(null); return; }
    let cancelled = false;
    setCampaignCheck({ plate: campaignPlate, status: "checking", campaigns: [] });
    setCampaignRequestSent(false);
    const timer = setTimeout(async () => {
      try {
        const res = await Promise.race([
          recallCampaignsApi.checkForBooking(orgId, data.immatriculation),
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error("timeout")), CAMPAIGN_CHECK_TIMEOUT_MS)),
        ]);
        if (!cancelled) setCampaignCheck({ plate: campaignPlate, status: res.status, campaigns: res.campaigns || [] });
      } catch {
        // Unreachable backend is not "no campaign" — never block on it.
        if (!cancelled) setCampaignCheck({ plate: campaignPlate, status: "unavailable", campaigns: [] });
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [campagneSelected, orgId, campaignPlate, data.immatriculation]);

  const sendCampaignRequest = async (payload: { nom: string; phone: string; email: string; message: string }) => {
    await recallCampaignsApi.sendRequest(orgId, {
      plate: data.immatriculation,
      ...payload,
    });
  };
  const isDiagnostic = selectedIntvs.some((i) => i.type === "diagnostic") || data.intervention_type === "diagnostic";
  // Express interventions (e.g. carrosserie EAD) are bookable as soon as the next day
  const effectiveMinDaysAhead = isExpress ? Math.min(minDaysAhead, 1) : minDaysAhead;
  // Total estimated duration is the sum of the selected interventions (matches the backend).
  const estimatedMinutes = selectedIntvs.reduce((sum, i) => sum + (i.estimated_minutes || 0), 0);
  // Configurable per-intervention immobilization delay (days); max across the selection.
  const restitutionMarginDays = selectedIntvs.reduce((max, i) => Math.max(max, i.min_days_margin || 0), 0);

  // Short interventions can be waited on site; long/diagnostic always leave the car.
  const isShort = !isDiagnostic && estimatedMinutes > 0 && estimatedMinutes < SHORT_THRESHOLD;
  const steps = buildSteps(isShort, data.wait_on_site);
  const stepCount = steps.length;
  const currentStep = steps[Math.min(step, steps.length - 1)];
  // If the step list shrinks (staff switches to "j'attends" from a later index),
  // keep the cursor in range.
  useEffect(() => {
    if (step > steps.length - 1) setStep(steps.length - 1);
  }, [steps.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefetch date availability while the user fills the vehicle step (step 1). The
  // service + intervention duration are already known here, so we warm the current
  // month's full-days and the day the date step will auto-select — making step 2
  // render instantly instead of showing "Chargement des disponibilités…".
  useEffect(() => {
    if (step !== 1 || !orgId) return;
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    bookingsApi
      .getFullDays(month, orgId, isFast || undefined, data.service || undefined, estimatedMinutes || undefined, urgentMode || undefined, isCampagne || undefined)
      .then((res) => {
        const minDate = computeMinDate(effectiveMinDaysAhead, isFast, srMinHoursAhead);
        const maxD = new Date(); maxD.setDate(maxD.getDate() + maxDaysAhead);
        const cand = firstCandidateDate(res.full_days, minDate, toLocalDateStr(maxD));
        if (cand) {
          bookingsApi
            .getAvailability(cand, data.service || "Mécanique", orgId, isFast || undefined, estimatedMinutes || undefined, urgentMode || undefined, isCampagne || undefined)
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [step, orgId, isFast, isCampagne, data.service, estimatedMinutes, urgentMode, effectiveMinDaysAhead, maxDaysAhead, srMinHoursAhead]);

  // Reasons the current step can't be validated — drives the button AND is shown
  // to staff so a disabled "Confirmer" is never a mystery. Prénom is NOT required
  // (hidden once the client is recognised; pros have none).
  const blockingReasons = (() => {
    const r: string[] = [];
    if (currentStep === "service") {
      if (!(data.interventions.length > 0 || data.custom_intervention.trim().length > 0))
        r.push("Choisissez au moins une intervention");
      // Une prestation marquée « précision requise » ne veut rien dire sans elle :
      // c'est la précision qui dit à l'atelier quoi chercher.
      for (const i of missingDetails) r.push(`Précisez la demande pour « ${i.name} »`);
    } else if (currentStep === "vehicle") {
      if (data.immatriculation.replace(/[\s-]/g, "").length < 7) r.push("Renseignez l'immatriculation du véhicule");
      if (!data.km.trim()) r.push("Renseignez le kilométrage du véhicule");
      if (data.payer_pending) r.push("Choisissez qui paie (le client du RDV)");
      // Campagne seule : sans campagne ouverte il n'y a rien à réaliser.
      if (isCampagne && campaignCheck?.plate === campaignPlate) {
        if (campaignCheck.status === "checking") r.push("Vérification des campagnes de rappel en cours…");
        else if (campaignCheck.status === "none") r.push("Aucune campagne ouverte sur ce véhicule — ajoutez une autre intervention ou envoyez une demande au garage");
      }
    } else if (currentStep === "date") {
      if (!data.deposit_date) r.push("Choisissez une date de dépôt");
      if (!data.deposit_time) r.push("Choisissez une heure de dépôt");
    } else if (currentStep === "mobility") {
      // Only the mobility choice is validated here — date/heure de récupération is the next step.
      if (isShort && data.wait_on_site === null) {
        r.push("Indiquez si le client attend sur place");
      } else if (isShort && data.wait_on_site) {
        if (!data.restitution_date) r.push("Date de restitution manquante");
      } else if (!data.mobility_option) {
        r.push("Choisissez une solution de mobilité");
      }
    } else if (currentStep === "restitution") {
      if (!data.restitution_date) r.push("Choisissez une date de restitution");
      if (!data.restitution_time) r.push("Choisissez une heure de récupération");
    } else if (currentStep === "contact") {
      // "recap" (Récapitulatif) is a read-only review — nothing to block.
      if (data.phone.replace(/\s/g, "").length < 10) r.push("Téléphone valide requis (10 chiffres)");
      if (!data.nom) r.push("Nom du client requis");
      if (!data.address.trim()) r.push("Adresse du client requise");
      if (!data.restitution_date) r.push("Date de restitution manquante");
      if (data.communication_channel === "email" && !data.email && !data.mute_client) r.push("Email requis pour notifier par email");
    } else if (currentStep === "cartegrise") {
      if (carteGriseChoice === null) r.push("Indiquez comment la carte grise est déposée");
      else if (carteGriseChoice === "staff" && !carteGriseFile) r.push("Ajoutez le fichier de la carte grise (PDF ou image)");
    }
    return r;
  })();
  const canProceed = blockingReasons.length === 0;

  // Which service category is drilled into (V2 grid -> detail pane). null = the grid.
  const [serviceSub, setServiceSub] = useState<string | null>(null);
  // Opening (or leaving) a category starts at the top, where its offer sits (cf. widget client).
  useEffect(() => { contentRef.current?.scrollTo({ top: 0 }); }, [serviceSub]);
  // Direction of the last navigation, so the step transition slides the way the user moved.
  const [navDir, setNavDir] = useState<"fwd" | "bck">("fwd");

  const handleNext = async () => {
    setNavDir("fwd");
    if (serviceSub) { setServiceSub(null); return; }
    if (step < stepCount - 1) {
      setStep(step + 1);
    } else {
      setSubmitting(true);
      setSubmitError(false);
      try {
        const result = await bookingsApi.create({
          org_id: orgId,
          plate: data.immatriculation,
          km: data.km ? parseInt(data.km.replace(/[^0-9]/g, ""), 10) || 0 : 0,
          service_type: SERVICES.find((s) => s.value === data.service)?.label || data.service || "Non précisé",
          intervention_type: data.intervention_type || "",
          interventions: data.interventions,
          custom_intervention: data.custom_intervention || undefined,
          internal_work: data.internal_work || undefined,
          arrived_by_assistance: data.arrived_by_assistance || undefined,
          intervention_details: Object.keys(data.intervention_details).length ? data.intervention_details : undefined,
          is_fast: isFast,
          express_24h: isExpress,
          mobility_option: data.mobility_option || "",
          wait_on_site: data.wait_on_site ?? false,
          tire_option: "",
          deposit_date: data.deposit_date || "",
          deposit_time: data.deposit_time,
          restitution_date: data.restitution_date || "",
          restitution_time: data.restitution_time || "",
          nom: data.nom,
          prenom: data.prenom,
          phone: data.phone.replace(/\s/g, "").replace(/^\+33/, "0"),
          email: data.email || undefined,
          address: data.address || undefined,
          communication_channel: data.communication_channel || "whatsapp",
          mute_client: data.mute_client || undefined,
          owner_change: data.owner_change || undefined,
          dossier_id: data.dossier_id || undefined,
          rental_vehicle_id: data.rental_vehicle_id || undefined,
          internal: true,
          force: forceSlot,
        });
        setBookingId(result.id);
        setOrdreId(result.ordre_id || "");
        setTrackingUrl((result as { tracking_url?: string }).tracking_url || "");
        setWhatsappPhone(result.whatsapp_phone || "");
        setWaConfirmationSent(!!result.whatsapp_confirmation_sent);

        // Carte grise dropped by staff — filed once the ordre exists. A failure here
        // must never look like a failed booking: the RDV is already created, so we
        // confirm it and offer a retry on the confirmation screen.
        if (carteGriseFile) {
          try {
            if (!result.ordre_id) throw new Error("ordre_id manquant");
            await ordresApi.uploadCarteGrise(result.ordre_id, carteGriseFile);
          } catch (e) {
            console.error("Carte grise upload failed:", e);
            setCarteGriseFailed(true);
            showNotification({ type: "error", message: "RDV créé, mais l'envoi de la carte grise a échoué" });
          }
        }

        setConfirmed(true);
        // Always show a success toast — some mounts close the modal in onSuccess
        // (e.g. QuickAppointmentModal), so the confirmation screen alone isn't enough.
        if (internalMode) showNotification({ type: "success", message: "RDV créé" });
        onSuccess?.(result.ordre_id || undefined);
      } catch (e) {
        console.error("Error submitting booking:", e);
        if (internalMode) showNotification({ type: "error", message: "Erreur lors de la création du RDV" });
        setSubmitError(true);
      }
      setSubmitting(false);
    }
  };

  /** Confirmation screen — the RDV exists, only the carte grise upload failed. */
  const retryCarteGrise = async () => {
    if (!ordreId || !carteGriseFile || carteGriseRetrying) return;
    setCarteGriseRetrying(true);
    try {
      await ordresApi.uploadCarteGrise(ordreId, carteGriseFile);
      setCarteGriseFailed(false);
      showNotification({ type: "success", message: "Carte grise déposée" });
    } catch (e) {
      console.error("Carte grise retry failed:", e);
      showNotification({ type: "error", message: "Envoi de la carte grise toujours impossible" });
    }
    setCarteGriseRetrying(false);
  };

  const handleBack = () => {
    setNavDir("bck");
    if (serviceSub) { setServiceSub(null); return; }
    if (step > 0) setStep(step - 1);
  };

  useEffect(() => {
    const el = footerRef.current;
    if (!el) { setFooterH(0); return; }
    const measure = () => setFooterH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [confirmed, submitError]);

  // ─── Layout ───────────────────────────────────────────────

  return (
    <div className="bkpage bkui" style={{
      maxHeight: "90vh", display: "flex", flexDirection: "column",
      fontFamily: "Inter, sans-serif", background: "#fff", borderRadius: 16,
      width: 480, maxWidth: "95vw", overflow: "hidden", position: "relative",
    }}>
      {/* Header — back, progress and close. The step title lives in the page's own h2. */}
      {!confirmed && (
        <div style={{ position: "sticky", top: 0, zIndex: 20, display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "#fff" }}>
          {(step > 0 || serviceSub) ? (
            <button className="bkui-iconbtn" onClick={handleBack} aria-label="Retour">
              <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
            </button>
          ) : <div style={{ width: 38, flexShrink: 0 }} />}
          <div style={{ flex: 1 }} />
          <span className="bkui-stepcount">{step + 1} / {stepCount}</span>
          <button className="bkui-iconbtn" onClick={onClose} aria-label="Fermer">
            <svg width={13} height={13} viewBox="0 0 16 16" fill="none"><path d="M2 2l12 12M14 2L2 14" stroke="#0A0A0A" strokeWidth={2} strokeLinecap="round" /></svg>
          </button>
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 2, background: "#EDEDEF" }} />
          <div style={{
            position: "absolute", left: 0, bottom: 0, height: 2,
            background: "#0A0A0A", borderRadius: "0 2px 2px 0",
            width: `${((step + 1) / stepCount) * 100}%`,
            transition: "width 0.4s cubic-bezier(.22,.61,.36,1)",
          }} />
        </div>
      )}
      {confirmed && (
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "10px 12px 0" }}>
          <button onClick={onClose} aria-label="Fermer" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4, lineHeight: 0 }}>
            <svg width={14} height={14} viewBox="0 0 16 16" fill="none"><path d="M2 2l12 12M14 2L2 14" stroke="#A1A1A1" strokeWidth={2} strokeLinecap="round" /></svg>
          </button>
        </div>
      )}

      {/* Content */}
      <div ref={contentRef} className="bkui-scroll" style={{
        flex: 1, display: "flex", flexDirection: "column",
        padding: `16px 20px ${footerH + 16}px`, overflowY: "auto",
        maxWidth: 480, width: "100%", margin: "0 auto",
      }}>
        {/* Keyed wrapper re-mounts on step change to play the slide-up transition */}
        <div key={confirmed ? "confirmed" : submitError ? "error" : `${step}-${serviceSub || ""}`} className={`bk-step bkui-${navDir}`}>
        {confirmed ? (
          <ConfirmationScreen data={data} bookingId={bookingId} trackingUrl={trackingUrl} whatsappPhone={whatsappPhone} orgConfig={orgConfig} internalMode={internalMode} onClose={onClose}
            whatsappConfirmationSent={waConfirmationSent}
            carteGriseStatus={carteGriseFailed ? "failed" : carteGriseFile ? "sent" : carteGriseChoice === "client" ? "client" : "none"}
            onRetryCarteGrise={retryCarteGrise} carteGriseRetrying={carteGriseRetrying} />
        ) : submitError ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, flex: 1, justifyContent: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#FEE2E2", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width={28} height={28} viewBox="0 0 16 16" fill="none">
                <path d="M2 2l12 12M14 2L2 14" stroke="#EF4444" strokeWidth={2} strokeLinecap="round" />
              </svg>
            </div>
            <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 18, color: "#000" }}>Erreur</span>
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: "#A1A1A1", textAlign: "center" }}>Une erreur est survenue. Veuillez réessayer.</span>
            <button onClick={() => setSubmitError(false)}
              style={{
                fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 14,
                padding: "14px 32px", borderRadius: 12, background: "#000",
                color: "#fff", border: "none", cursor: "pointer",
              }}>
              Réessayer
            </button>
          </div>
        ) : (
          <>
            {currentStep === "service" && <StepService data={data} update={update} interventions={interventions} sub={serviceSub} setSub={setServiceSub} internalMode={internalMode} promotions={promotions} />}
            {currentStep === "vehicle" && <StepVehicle data={data} update={update} orgId={orgId} clientQuery={clientQuery} setClientQuery={setClientQuery} clientVehicles={clientVehicles} setClientVehicles={setClientVehicles} addingVehicle={addingVehicle} setAddingVehicle={setAddingVehicle} payerOptions={payerOptions} setPayerOptions={setPayerOptions} payerKey={payerKey} setPayerKey={setPayerKey} campagneSelected={campagneSelected} campagneOnly={isCampagne} campaignCheck={campaignCheck} campaignRequestSent={campaignRequestSent} setCampaignRequestSent={setCampaignRequestSent} onSendCampaignRequest={sendCampaignRequest} prefill={pendingPrefill} onPrefillUsed={() => setPendingPrefill(null)} />}
            {currentStep === "date" && <StepDateTime data={data} update={update} orgId={orgId} minDaysAhead={effectiveMinDaysAhead} maxDaysAhead={maxDaysAhead} isFast={isFast} isCampagne={isCampagne} srMinHoursAhead={srMinHoursAhead} estimatedMinutes={estimatedMinutes} onEstimatedReadyTimes={setEstimatedReadyTimes} urgent={urgentMode} />}
            {currentStep === "mobility" && <StepRecap section="mobility" data={data} update={update} estimatedMinutes={estimatedMinutes} safetyMarginHours={safetyMarginHours} isDiagnostic={isDiagnostic} restitutionMarginDays={restitutionMarginDays} estimatedReadyTimes={estimatedReadyTimes} orgConfig={orgConfig}force={forceSlot} />}
            {currentStep === "restitution" && <StepRecap section="restitution" data={data} update={update} estimatedMinutes={estimatedMinutes} safetyMarginHours={safetyMarginHours} isDiagnostic={isDiagnostic} restitutionMarginDays={restitutionMarginDays} estimatedReadyTimes={estimatedReadyTimes} orgConfig={orgConfig}force={forceSlot} />}
            {currentStep === "recap" && <StepRecap section="recap" data={data} update={update} estimatedMinutes={estimatedMinutes} safetyMarginHours={safetyMarginHours} isDiagnostic={isDiagnostic} restitutionMarginDays={restitutionMarginDays} estimatedReadyTimes={estimatedReadyTimes} orgConfig={orgConfig}force={forceSlot} />}
            {currentStep === "contact" && <StepContact data={data} update={update} orgId={orgId} />}
            {currentStep === "cartegrise" && <StepCarteGrise choice={carteGriseChoice} setChoice={setCarteGriseChoice} file={carteGriseFile} setFile={setCarteGriseFile} plate={data.immatriculation} check={carteGriseCheck} setCheck={setCarteGriseCheck} />}
          </>
        )}
        </div>
      </div>

      {/* Fausse barre de défilement : la vraie est masquée (le formulaire se lit mieux
          sans), mais sans elle rien ne disait qu'il restait des cartes sous le pied. */}
      <BookingScrollHint scrollerRef={contentRef} bottomInset={footerH} watchKey={`${step}-${serviceSub || ""}-${confirmed}-${submitError}`} />

      {/* Footer */}
      {!confirmed && !submitError && (
        <div ref={footerRef} className="bkui-bottom" style={{
          position: "absolute", left: 0, right: 0, bottom: 0, zIndex: 10,
          padding: "12px 20px 20px",
          maxWidth: 480, width: "100%", margin: "0 auto",
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          {/* Any internal user may force a full slot (bypass atelier capacity + the
              daily reception quota, so any open day can be picked) — the reception desk
              takes urgent cars without a manager on hand. Offered on the two steps
              that actually pick a slot — the drop-off and the handover — because an
              urgent job often needs the car back the same day, which the immobilisation
              margin would otherwise forbid. On the steps in between, flipping it would
              change what gets booked without re-opening any list, so there we only
              remind that this RDV is forced. */}
          {internalMode && (currentStep === "date" || currentStep === "restitution") && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 10, background: forceSlot ? "#FEF2F2" : "#FAFAFA", border: `1px solid ${forceSlot ? "#FECACA" : "#E5E5E5"}` }}>
              <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 12, color: forceSlot ? "#991B1B" : "#333" }}>Urgence — forcer l'atelier</span>
                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 10, color: "#A1A1A1" }}>
                  {currentStep === "restitution"
                    ? "Ignore la marge d'immobilisation et les créneaux occupés — restitution le jour même possible."
                    : "Ignore la capacité atelier et le quota du jour. Les créneaux déjà pris en réception restent bloqués."}
                </span>
              </div>
              <Switch checked={forceSlot} onChange={(v) => { setForceSlot(v); setUrgentMode(v); }} />
            </div>
          )}

          {/* Steps without the switch: the warning stays. */}
          {forceSlot && currentStep !== "date" && currentStep !== "restitution" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 10, background: "#FEF2F2", border: "1px solid #FECACA" }}>
              <svg width={14} height={14} viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                <path d="M8 1.6 15.2 14.4H0.8L8 1.6Z" stroke="#991B1B" strokeWidth={1.4} strokeLinejoin="round" />
                <path d="M8 6.2v3.4" stroke="#991B1B" strokeWidth={1.4} strokeLinecap="round" />
                <circle cx={8} cy={11.9} r={0.85} fill="#991B1B" />
              </svg>
              <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 12, color: "#991B1B" }}>RDV forcé — capacité atelier et quota du jour ignorés</span>
            </div>
          )}

          {/* Why the button is disabled */}
          {!submitting && blockingReasons.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: "8px 10px", borderRadius: 10, background: "#F7F7F7" }}>
              <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 11, color: "#374151" }}>Pour continuer :</span>
              {blockingReasons.map((reason) => (
                <span key={reason} style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "#6B7280" }}>• {reason}</span>
              ))}
            </div>
          )}

          <button
            className="bkui-cta"
            onClick={handleNext}
            disabled={submitting || missingDetails.length > 0 || (!serviceSub && !canProceed)}>
            {submitting ? "Envoi…"
              : serviceSub ? (data.interventions.length ? `Ajouter (${data.interventions.length})` : "Retour")
              : step === stepCount - 1 ? "Confirmer la réservation" : "Continuer"}
          </button>

        </div>
      )}

      {/* Sending overlay — covers the whole card. NOT position:fixed, and NOT inside the
          bottom bar: `.bkui-bottom` carries a backdrop-filter, which makes it the
          containing block of any fixed descendant. The overlay was then laid out inside
          the footer strip and came out crushed against the bottom edge. */}
      {submitting && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 10001,
          background: "rgba(255,255,255,0.97)",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: 22, padding: 24,
        }}>
          {/* A spinning ring above the bar: the ring says "it is still working"
              (it never stops), the bar fills over ~10s so the wait reads as
              progress and not as a frozen screen during the ServiceBox transfer.
              The reduced-motion rule in booking.css flattens every animation under
              .bkui — the ring keeps spinning, it is the only sign of life here. */}
          <style>{`@keyframes bkSubmitProgress{from{width:0%}to{width:100%}}@keyframes bkSubmitSpin{to{transform:rotate(360deg)}}@keyframes bkSubmitPulse{0%,100%{opacity:.55}50%{opacity:1}}
            @media (prefers-reduced-motion: reduce){.bkui [data-bk-submit-ring]{animation-duration:.85s!important}}`}</style>
          <div data-bk-submit-ring style={{
            width: 52, height: 52, borderRadius: "50%",
            border: "3px solid #ECECEC", borderTopColor: "#111",
            animation: "bkSubmitSpin 0.85s linear infinite",
          }} />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, width: "100%" }}>
            <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: 18, color: "#000" }}>Envoi en cours…</span>
            <div style={{ width: "min(100%, 260px)", height: 6, borderRadius: 999, background: "#EEE", overflow: "hidden" }}>
              <div style={{ height: "100%", borderRadius: 999, background: "#111", animation: "bkSubmitProgress 10s cubic-bezier(0.25,0.8,0.35,1) forwards" }} />
            </div>
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#9A9A9A", textAlign: "center", animation: "bkSubmitPulse 1.8s ease-in-out infinite" }}>Cela peut prendre quelques secondes</span>
          </div>
        </div>
      )}
    </div>
  );
}
